import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  ThemeProvider,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  setNodes,
  setEdges,
  setFinalWorkflowJson,
  loadWorkflow,
  saveWorkflow,
  setTriggerResponse,
  markWorkflowDirty,
  clearWorkflowDirty,
} from '../state/slices/workflowSlice';
import NodeConfigurationPanel from './NodeConfigurationModal';
import BacktrackConfigurationModal from './BacktrackConfigurationModal';
import { theme } from '../style/theme';
import SourceNode from './customizedNodes/SourceNode';
import DestinationNode from './customizedNodes/DestinationNode';
import TransformationNode from './customizedNodes/TransformationNode';
import ProcessorNode from './customizedNodes/ProcessorNode';
import IntegrationNode from './customizedNodes/IntegrationNode';
import BacktrackNode from './customizedNodes/BacktrackNode';
import ActionButtons from './ActionButtons';
import Sidebar from './SideDrawer';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EntitySelectionDialog from './Component/EntitySelectionDialog';
import SqlHostPortDialog from './Component/SqlHostPortDialog';
import { useThemeContext } from '../Context/ThemeContext';
import {
  removeEntitiesForKey,
  setConfirmedDestinationEntities,
  setConfirmedSourceEntities,
  setSelectedDestinationEntities,
  setSelectedSourceEntities,
} from '../state/slices/entitiesSlice';
import { toast } from 'react-toastify';
import {
  removeFinalConfigById,
  resetMappingForWorkflow,
  setFilterExpressionForWorkflow,
} from '../state/slices/mappingSlice';
import {
  postWorkflowToBackend,
  triggerExecuteWorkflow,
} from '../apis/Workflows';
import store from '../state/store';
import ConfirmDialog from './Component/ConfirmDialog';
import { v4 as uuidv4 } from 'uuid';
import { convertOldToNewWorkflow } from '../utils/formatters/workflowFormatter';
import { convertOldMappingsToNew } from '../utils/formatters/convertOldMappingsToNew';
import DecisionConfigurationModal from './DecisionConfigurationModal';
import ProceedMappingDialog from './ProceedMappingDialog';
import LShapeEdge from './LShapeEdge';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoDialog from './Component/InfoDialog';
import CloseIcon from '@mui/icons-material/Close';
// import IntegrationPanelPill from './IntegretionPanelPill';
import RightSideDrawer from './RightSideDrawer';
import SideAwareEdge from './Component/Edges/SideAwareEdge';
import WorkflowActionBar from './WorkflowActionBar';

const sanitizeConnectionPayload = (connection = {}) => {
  const normalizeNullish = (value) =>
    value === undefined || value === null || value === '' ? null : value;

  if (!connection || typeof connection !== 'object') {
    return {
      api_version: null,
      conn_id: null,
      conn_name: '',
    };
  }

  return {
    api_version: normalizeNullish(connection.api_version),
    conn_id: normalizeNullish(
      Object.prototype.hasOwnProperty.call(connection, 'conn_id')
        ? connection.conn_id
        : null,
    ),
    conn_name: Object.prototype.hasOwnProperty.call(connection, 'conn_name')
      ? connection.conn_name
      : '',
  };
};

const sanitizeNodeConfigPayload = (config = {}) => {
  if (!config || typeof config !== 'object') return {};

  const sanitized = Object.entries(config).reduce((acc, [key, value]) => {
    if (key === 'connection') {
      acc.connection = sanitizeConnectionPayload(value);
      return acc;
    }
    if (
      key === 'sqlMetadata' ||
      key === 'connector_name' ||
      key === 'environment'
    ) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  if (config.connection && !sanitized.connection) {
    sanitized.connection = sanitizeConnectionPayload(config.connection);
  }

  return sanitized;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const sanitizeEntityListPayload = (entities = []) =>
  toArray(entities)
    .map((entity) => {
      if (!entity || typeof entity !== 'object') return null;
      const entityName = entity.entity_name || entity.entity || '';
      if (!entityName) return null;
      return {
        entity_name: entityName,
        label: entity.label || entityName,
      };
    })
    .filter(Boolean);

const sanitizeEntityMapPayload = (entityMap = {}) => {
  if (!entityMap || typeof entityMap !== 'object') return {};
  return Object.entries(entityMap).reduce((acc, [key, list]) => {
    acc[key] = sanitizeEntityListPayload(list);
    return acc;
  }, {});
};

const ETLWorkflow = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    nodes = [],
    edges = [],
    workflows = {},
  } = useSelector((state) => state.workflows || {});
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const { darkMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState(null);
  const [pendingDeleteNodeIds, setPendingDeleteNodeIds] = useState([]);
  const [pendingDeleteNodeLabel, setPendingDeleteNodeLabel] = useState('');
  const [sqlHostPortDialog, setSqlHostPortDialog] = useState(null);

  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [requiredEntityCount, setRequiredEntityCount] = useState(0);
  const [enableBacktrack, setEnableBacktrack] = useState(false);

  // Auto-enable/disable feature flag based on backtrack node presence
  useEffect(() => {
    const hasBacktrackNodes = nodes.some((n) => n.type === 'backtrackNode');
    if (hasBacktrackNodes !== enableBacktrack) {
      setEnableBacktrack(hasBacktrackNodes);
    }
  }, [nodes, enableBacktrack]);

  const edgeTypes = {
    lshape: LShapeEdge,
    sideaware: SideAwareEdge,
  };

  console.log('nodes::', nodes);

  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDialogData, setMappingDialogData] = useState({
    source: '',
    dest1: '',
    dest2: '',
  });
  const handleCloseMapping = () => setMappingDialogOpen(false);

  const { workflowName: workflowId } = useParams();

  // eslint-disable-next-line
  const [workflowname, setWorkflowName] = useState(workflowId);
  // const workflowData = useSelector(state => state.workflows.workflows[workflowname]);

  const [collapsedBoth, setCollapsedBoth] = useState(false);

  const workflowData = workflows?.[workflowId];
  const unsavedFlag = useSelector(
    (state) => state.workflows?.unsavedChangesByWorkflow?.[workflowId],
  );

  console.log('workflowData data:', workflowData);

  const scheduleDateTime = workflowData?.scheduleDateTime;
  const timeZone = workflowData?.timezone;

  console.log('schedule date:', scheduleDateTime);
  console.log('timeZone:', timeZone);

  // const workflowId = workflows?.[workflowname]?.workflowId;
  const workflowConfigurationId =
    workflows?.[workflowId]?.workflowConfigurationId;
  const workflowRunId = workflows?.[workflowId]?.workflowRunId;

  console.log('workflowData:', workflowData);
  console.log('workflowId:', workflowId);
  console.log('workflowRunId:', workflowRunId);
  console.log('workflowConfigurationId:', workflowConfigurationId);

  const selectedSourceEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const selectedDestinationEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedDestinationEntitiesByWorkflow?.[workflowId] || {},
  );
  const confirmedSourceEntities = useSelector(
    (state) =>
      state.entities?.confirmedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const confirmedDestinationEntities = useSelector(
    (state) =>
      state.entities?.confirmedDestinationEntitiesByWorkflow?.[workflowId] ||
      {},
  );

  const sanitizedEntitySelections = useMemo(
    () => ({
      source: sanitizeEntityMapPayload(confirmedSourceEntities),
      destination: sanitizeEntityMapPayload(confirmedDestinationEntities),
    }),
    [confirmedSourceEntities, confirmedDestinationEntities],
  );

  // eslint-disable-next-line
  const [nodeConfigForEntitySelection, setNodeConfigForEntitySelection] =
    useState(null);

  const [openDecisionDrawer, setOpenDecisionDrawer] = useState(false);
  const [decisionFields, setDecisionFields] = useState([]);
  const [clearSourceSelection, setClearSourceSelection] = useState(0);

  const sidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const actionRef = useRef(null);
  const dirtySnapshotRef = useRef(null);
  const dirtyInitializedRef = useRef(false);
  const singleSourceMultiDestToastShownRef = useRef(false);

  const mappingConnectionDetails = useSelector(
    (state) =>
      state.mapping.mappingsByWorkflow?.[workflowId]?.connectionDetails,
  );
  const workflowConnectionDetails = workflows?.[workflowId]?.connectionDetails;

  const connectionDetails =
    mappingConnectionDetails && mappingConnectionDetails.length > 0
      ? mappingConnectionDetails
      : (workflowConnectionDetails ?? []);

  const dirtySnapshot = useMemo(() => {
    try {
      return JSON.stringify({
        nodes,
        edges,
        connectionDetails,
        scheduleDateTime: workflowData?.scheduleDateTime ?? null,
        timezone: workflowData?.timezone ?? null,
        frequency: workflowData?.frequency ?? null,
        endDate: workflowData?.endDate ?? null,
        customDays: workflowData?.customDays ?? null,
        hourInterval: workflowData?.hourInterval ?? null,
      });
    } catch (err) {
      console.warn('Failed to compute dirty snapshot:', err);
      return null;
    }
  }, [
    nodes,
    edges,
    connectionDetails,
    workflowData?.scheduleDateTime,
    workflowData?.timezone,
    workflowData?.frequency,
    workflowData?.endDate,
    workflowData?.customDays,
    workflowData?.hourInterval,
  ]);

  console.log('connectionDetails:', connectionDetails);

  useEffect(() => {
    dirtySnapshotRef.current = null;
    dirtyInitializedRef.current = false;
  }, [workflowId]);

  useEffect(() => {
    if (!workflowId || !dirtySnapshot) return;

    if (dirtySnapshotRef.current === null) {
      dirtySnapshotRef.current = dirtySnapshot;
      return;
    }

    if (!dirtyInitializedRef.current && unsavedFlag === false) {
      dirtySnapshotRef.current = dirtySnapshot;
      dirtyInitializedRef.current = true;
      return;
    }

    dirtyInitializedRef.current = true;

    if (dirtySnapshot !== dirtySnapshotRef.current) {
      dirtySnapshotRef.current = dirtySnapshot;
      dispatch(markWorkflowDirty({ workflowId }));
    }
  }, [dirtySnapshot, workflowId, dispatch, unsavedFlag]);

  const processWorkflowData = () => {
    const formattedNodes = nodes.map((node) => {
      const match = node.data.label.match(/(.*?)\s*\((.*?)\)/);
      const label = match ? match[1].trim() : node.data.label;
      const type = match ? match[2].trim().toLowerCase() : node.type;

      return {
        id: node.id,
        position: node.position,
        label,
        type,
        config:
          typeof node.data.config === 'object' ? { ...node.data.config } : {},
      };
    });

    const formattedEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      animated: edge.animated,
      label: edge.label || '',
      markerEnd: {
        type: 'arrowclosed',
        width: 20,
        height: 20,
        color: darkMode ? '#3a8eff' : '#1976d2', // Adjust color based on theme
      },
    }));

    return { nodes: formattedNodes, edges: formattedEdges };
  };

  const processWorkflowDatas = useMemo(
    () => processWorkflowData(),
    [JSON.stringify(nodes), JSON.stringify(edges)],
  );

  // console.log('Processed Data to Send:', JSON.stringify(processWorkflowDatas, null, 2));

  const WorkflowSaveJson = () => {
    const toTitle = (value = '') => {
      if (typeof value !== 'string') return '';
      const trimmed = value.trim();
      if (!trimmed) return '';
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    const nodeTypeLabel = (type = '') => {
      if (!type) return '';
      const clean = type.replace(/Node$/i, '');
      if (!clean) return '';
      return clean.charAt(0).toUpperCase() + clean.slice(1);
    };

    const deriveLabel = (node) => {
      const dataLabel =
        typeof node?.data?.label === 'string' ? node.data.label.trim() : '';
      const configLabel =
        typeof node?.data?.config?.label === 'string'
          ? node.data.config.label.trim()
          : '';
      const fallbackLabel =
        typeof node?.label === 'string' ? node.label.trim() : '';

      if (dataLabel) return dataLabel;
      if (configLabel) return configLabel;
      if (fallbackLabel) return fallbackLabel;

      const connection = node?.data?.config?.connection || {};
      const connectorRaw =
        connection.connector_name ||
        connection.connector ||
        connection.type ||
        '';
      const connector = toTitle(connectorRaw) || 'Connector';
      const suffix = nodeTypeLabel(node?.type);
      return suffix ? `${connector} (${suffix})` : connector;
    };

    const formattedNodes = nodes.map((node) => {
      const config =
        node?.data && typeof node.data.config === 'object'
          ? { ...node.data.config }
          : {};
      const label = deriveLabel(node);

      if (!config.label) {
        config.label = label;
      }

      const connectorRaw =
        config?.connection?.connector_name ||
        config?.connection?.connector ||
        config?.connection?.type ||
        '';
      const moduleRaw = typeof config.module === 'string' ? config.module : '';
      const labelContainsSql =
        typeof label === 'string' && label.toLowerCase().includes('sql');
      const isSqlConnector =
        connectorRaw?.toLowerCase() === 'sql' ||
        moduleRaw?.toLowerCase() === 'sql' ||
        labelContainsSql;

      if (isSqlConnector) {
        // Always set id, type, module, and label in config to match Salesforce format
        config.id = node.id;
        // Normalize type to match Salesforce format: "sourceNode" or "destinationNode"
        config.type =
          node.type === 'source'
            ? 'sourceNode'
            : node.type === 'destination'
              ? 'destinationNode'
              : node.type;
        const normalizedModule = moduleRaw ? moduleRaw.toLowerCase() : 'sql';
        config.module = normalizedModule;
        // Ensure label is set for SQL nodes in Salesforce format: "SQL (Source)" or "SQL (Destination)"
        if (!config.label) {
          const nodeTypeLabel =
            node.type === 'sourceNode' || node.type === 'source'
              ? 'Source'
              : node.type === 'destinationNode' || node.type === 'destination'
                ? 'Destination'
                : '';
          config.label = nodeTypeLabel ? `SQL (${nodeTypeLabel})` : 'SQL';
        }
      }

      return {
        id: node.id,
        label,
        type: node.type,
        position: node.position,
        config: sanitizeNodeConfigPayload(config),
      };
    });

    // Format edges with only minimal required fields
    const formattedEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: edge.type || 'default',
      animated: edge.animated ?? true,
      markerEnd: edge.markerEnd || {
        type: 'arrowclosed',
        width: 20,
        height: 20,
        color: '#1976d2',
      },
    }));

    const GenericTransform = nodes
      .filter((node) => node.type === 'transformationNode')
      .map((node) => node.data?.config?.transform || {})
      .filter((t) => Object.keys(t).length > 0);

    return {
      nodes: formattedNodes,
      edges: formattedEdges,
      GenericTransform,
      mapping: connectionDetails,
      entitySelections: sanitizedEntitySelections,
    };
  };

  const saveJson = useMemo(
    () => WorkflowSaveJson(),
    [
      JSON.stringify(nodes),
      JSON.stringify(edges),
      JSON.stringify(connectionDetails),
    ],
  );

  const { nodes: formattedNodes, edges: formattedEdges } =
    convertOldToNewWorkflow(saveJson);

  // Read any confirmed filterExpression stored for this workflow
  const filterExpression = useSelector(
    (state) => state.mapping?.filterExpressionByWorkflow?.[workflowId],
  );

  useEffect(() => {
    if (filterExpression) return;
    try {
      const raw = localStorage.getItem(`workflow_filters:${workflowname}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          parsed.filterGroup &&
          Array.isArray(parsed.filterGroup.conditions)
        ) {
          dispatch(
            setFilterExpressionForWorkflow({
              workflowName: workflowname,
              filterExpression: parsed,
            }),
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [filterExpression, workflowname, dispatch]);

  const formattedNodesWithFilter = useMemo(() => {
    try {
      return formattedNodes.map((n) => {
        if (n.type === 'sourceNode' || n.type === 'source') {
          const existingConfig = n?.data?.config || {};
          const nextConfig = { ...existingConfig };

          if (filterExpression) {
            const group = filterExpression?.filterGroup
              ? filterExpression.filterGroup
              : filterExpression;

            if (Array.isArray(group?.conditions)) {
              nextConfig.filterExpression = {
                filterGroup: {
                  ...(group.conditions.length > 1
                    ? {
                        condition_operator:
                          group.condition_operator || group.logic || 'AND',
                      }
                    : {}),
                  conditions: group.conditions.map((c) => ({
                    entity_name: c.entity_name,
                    source_field: c.source_field,
                    operator: c.operator,
                    input_value: c.input_value,
                    ...(c.connection_scope
                      ? { connection_scope: c.connection_scope }
                      : {}),
                  })),
                },
              };
            } else if (
              group?.entity_name &&
              group?.source_field &&
              group?.operator !== undefined
            ) {
              nextConfig.filterExpression = {
                filterGroup: {
                  conditions: [
                    {
                      entity_name: group.entity_name,
                      source_field: group.source_field,
                      operator: group.operator,
                      input_value: group.input_value,
                      ...(group.connection_scope
                        ? { connection_scope: group.connection_scope }
                        : {}),
                    },
                  ],
                },
              };
            }
          } else {
            if ('filterExpression' in nextConfig) {
              delete nextConfig.filterExpression;
            }
          }

          return {
            ...n,
            data: {
              ...n.data,
              config: nextConfig,
            },
          };
        }
        return n;
      });
    } catch (e) {
      return formattedNodes;
    }
  }, [formattedNodes, filterExpression]);

  console.log('formattedNodes:::', formattedNodesWithFilter);

  useEffect(() => {
    if (filterExpression) return;
    try {
      const srcNode = formattedNodesWithFilter.find(
        (n) =>
          (n.type === 'sourceNode' || n.type === 'source') &&
          n?.data?.config?.filterExpression?.filterGroup &&
          Array.isArray(
            n.data.config.filterExpression.filterGroup.conditions,
          ) &&
          n.data.config.filterExpression.filterGroup.conditions.length > 0,
      );
      if (srcNode) {
        const group = srcNode.data.config.filterExpression.filterGroup;
        dispatch(
          setFilterExpressionForWorkflow({
            workflowName: workflowname,
            filterExpression: { filterGroup: group },
          }),
        );
      }
    } catch (e) {
      console.log('Error setting filter expression from source node:', e);
    }
  }, [filterExpression, formattedNodesWithFilter, workflowname, dispatch]);

  const nodesById = Object.fromEntries(
    formattedNodesWithFilter.map((n) => [
      n.id,
      n.config ? { ...n, config: n.config } : n,
    ]),
  );

  console.log('nodesById:::', nodesById);

  const mappings = convertOldMappingsToNew(saveJson.mapping, nodesById);

  console.log('mappingss:', mappings);

  // working code of dynamic tabs function

  // const handleTabChange = (event, newValue) => {
  //   setSelectedFile(newValue);
  //   handleLoadWorkflow(newValue);
  // };

  const handleRequestDeleteNode = (nodeId, nodeLabel) => {
    setPendingDeleteNodeId(nodeId);
    setPendingDeleteNodeLabel(nodeLabel);
    setConfirmDialogOpen(true);
  };

  // Auto-disable feature flag when all backtrack nodes are removed
  useEffect(() => {
    const hasBacktrackNodes = nodes.some((n) => n.type === 'backtrackNode');
    if (!hasBacktrackNodes && enableBacktrack) {
      // Only auto-disable if it was auto-enabled (not manually set from backend)
      // Check if it came from backend by looking at loaded workflow
      const loadedWorkflow = workflows[workflowId];
      const wasFromBackend =
        loadedWorkflow?.workflowJson?.featureFlags?.enableBacktrack ||
        loadedWorkflow?.config?.featureFlags?.enableBacktrack ||
        loadedWorkflow?.featureFlags?.enableBacktrack;

      if (!wasFromBackend) {
        setEnableBacktrack(false);
      }
    }
  }, [nodes, enableBacktrack, workflows, workflowId]);

  const nodeTypes = {
    sourceNode: (props) => (
      <SourceNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    destinationNode: (props) => (
      <DestinationNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    transformationNode: (props) => (
      <TransformationNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    processorNode: (props) => (
      <ProcessorNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    integrationNode: (props) => (
      <IntegrationNode
        {...props}
        deleteNode={handleRequestDeleteNode}
        updatedConfig={processWorkflowDatas}
      />
    ),
    backtrackNode: (props) => (
      <BacktrackNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
  };

  const cleanLabel = (s) => String(s || '').replace(/\s*\(.*?\)\s*$/, '');

  // Prefer showing the specific connection name when available
  const nodeConnectionName = (n) => {
    const conn = n?.data?.config?.connection || n?.config?.connection;
    return conn?.conn_name || conn?.name || null;
  };

  const nodeDisplayName = (n) => {
    if (!n) return '';
    if (n.label) return cleanLabel(n.label);
    if (n.data?.label) return cleanLabel(n.data.label);
    if (n.data?.config?.label) return cleanLabel(n.data.config.label);
    return n.id || '';
  };

  const isDestNode = (n) =>
    n?.type === 'destination' ||
    n?.type === 'destinationNode' ||
    n?.config?.type === 'destinationNode';
  const isSourceNode = (n) =>
    n?.type === 'source' ||
    n?.type === 'sourceNode' ||
    n?.config?.type === 'sourceNode';
  const findDecisionNode = (nodesArr) =>
    nodesArr.find(
      (n) =>
        String(n.id || '').startsWith('decision-') || !!n?.config?.decision,
    ) || null;

  // const getMappingEndpoints = (decisionNode, nodes, edges) => {
  //   if (!decisionNode)
  //     return {
  //       sourceId: null,
  //       dest1Id: null,
  //       dest2Id: null,
  //       source: '',
  //       dest1: '',
  //       dest2: '',
  //     };

  //   // inbound: find source node
  //   const inEdges = edges.filter((e) => e.target === decisionNode.id);
  //   const sourceNode = nodes.find(
  //     (n) => n.type === 'sourceNode' && inEdges.some((e) => e.source === n.id)
  //   );

  //   // outbound: first two destination nodes
  //   const outEdges = edges.filter((e) => e.source === decisionNode.id);
  //   const destNodes = outEdges
  //     .map((e) => nodes.find((n) => n.id === e.target))
  //     .filter((n) => n && n.type === 'destinationNode');

  //   const dest1 = destNodes[0] || null;
  //   const dest2 = destNodes[1] || null;

  //   return {
  //     sourceId: sourceNode?.id || null,
  //     dest1Id: dest1?.id || null,
  //     dest2Id: dest2?.id || null,
  //     source: nodeDisplayName(sourceNode),
  //     dest1: nodeDisplayName(dest1),
  //     dest2: nodeDisplayName(dest2),
  //   };
  // };

  const getMappingEndpoints = (decisionNode, nodes, edges) => {
    if (!decisionNode)
      return {
        sourceId: null,
        dest1Id: null,
        dest2Id: null,
        source: '',
        dest1: '',
        dest2: '',
      };

    const inEdges = edges.filter((e) => e.target === decisionNode.id);
    // const sourceNode = nodes.find(
    //   (n) => n.type === 'sourceNode' && inEdges.some((e) => e.source === n.id)
    // );

    const outEdges = edges.filter((e) => e.source === decisionNode.id);

    // const destNodes = outEdges
    //   .map((e) => nodes.find((n) => n.id === e.target))
    //   .filter((n) => n && n.type === 'destinationNode');

    const sourceNode = nodes.find(
      (n) => isSourceNode(n) && inEdges.some((e) => e.source === n.id),
    );
    const destNodes = outEdges
      .map((e) => nodes.find((n) => n.id === e.target))
      .filter(isDestNode);

    const destIds = destNodes.map((d) => d.id);
    const destNames = destNodes.map((d) => nodeDisplayName(d));
    const sourceName =
      nodeConnectionName(sourceNode) || nodeDisplayName(sourceNode);
    return {
      decisionNodeId: decisionNode.id,
      sourceId: sourceNode?.id || null,
      dest1Id: destNodes[0]?.id || null,
      dest2Id: destNodes[1]?.id || null,
      destIds,
      destNames,
      source: sourceName,
      dest1: destNames[0] || '',
      dest2: destNames[1] || '',
    };
  };

  // const handleProceedAndNavigate = async (overrideDestId = null) => {
  //   setMappingDialogOpen(false);

  //   const updatedConfig = processWorkflowDatas;
  //   const nodes = updatedConfig?.nodes || [];
  //   const edges = updatedConfig?.edges || [];

  //   const { sourceId, dest1Id, source, dest1, dest2 } = mappingDialogData || {};

  //   const allConfigs =
  //     nodes
  //       ?.map((node) => {
  //         const connId = node.config?.connection?.conn_id || '';
  //         const mod = node.config?.module || '';
  //         const nodeKey = `${node.id}-${connId}-${mod}`;
  //         const isSource = node.type === 'sourceNode' || node.type === 'source';
  //         const isDestination =
  //           node.type === 'destinationNode' || node.type === 'destination';
  //         if (!(isSource || isDestination)) return null;
  //         return {
  //           id: node.id,
  //           label: node.config?.label,
  //           connection: node.config?.connection,
  //           module: node.config?.module,
  //           selectedEntities: isSource
  //             ? selectedSourceEntitiesByWorkflow[nodeKey] || []
  //             : selectedDestinationEntitiesByWorkflow[nodeKey] || [],
  //           type: isSource ? 'source' : 'destination',
  //         };
  //       })
  //       .filter(Boolean) || [];

  //   const resolvedSourceId =
  //     sourceId || allConfigs.find((c) => c.type === 'source')?.id || null;

  //   const targetDestId =
  //     typeof overrideDestId === 'string' || typeof overrideDestId === 'number'
  //       ? overrideDestId
  //       : dest1Id;

  //   const filtered = allConfigs.filter((c) => {
  //     if (c.type === 'source')
  //       return resolvedSourceId ? c.id === resolvedSourceId : true;
  //     if (c.type === 'destination') return c.id === targetDestId;
  //     return false;
  //   });

  //   const srcEntities = filtered
  //     .filter((c) => c.type === 'source')
  //     .flatMap((c) => c.selectedEntities || []);
  //   const dstEntities = filtered
  //     .filter((c) => c.type === 'destination')
  //     .flatMap((c) => c.selectedEntities || []);

  //   const decisionNode = findDecisionNode(nodes);

  //   let decisionMeta = null;
  //   let nextDestId = null;

  //   console.log('nodes::::', nodes);

  //   if (decisionNode) {
  //     const destNodes = edges
  //       .filter((e) => e.source === decisionNode.id)
  //       .map((e) => nodes.find((n) => n.id === e.target))
  //       .filter(isDestNode);

  //     const destIds = destNodes.map((d) => d.id);
  //     const destNames = destNodes.map(
  //       (d) => d?.config?.label || d?.label || ''
  //     );

  //     const baseDest =
  //       typeof overrideDestId === 'string' || typeof overrideDestId === 'number'
  //         ? overrideDestId
  //         : dest1Id || destIds[0];

  //     const currentIndex = Math.max(0, destIds.indexOf(baseDest));
  //     nextDestId = destIds[currentIndex + 1] ?? null;

  //     decisionMeta = {
  //       decisionNodeId: decisionNode.id,
  //       sourceId: resolvedSourceId,
  //       destIds,
  //       destNames,
  //       currentIndex,
  //     };
  //   }

  //   console.log('decisionMeta1:', decisionMeta);

  //   console.log('decisionMeta2:', decisionMeta);

  //   const canGoNext = !!nextDestId;

  //   navigate(`/parentworkflow/${workflowname || 'defaultWorkflow'}/mapping`, {
  //     state: {
  //       finalConfigPayload: filtered,
  //       selectedSourceEntities: srcEntities,
  //       selectedDestinationEntities: dstEntities,
  //       canGoNext,
  //       primaryDestinationId: targetDestId,
  //       secondaryDestinationId: nextDestId,
  //       decisionMeta,

  //       sourceName: source,
  //       dest1Name: dest1,
  //       dest2Name: dest2,
  //     },
  //   });
  // };

  const handleProceedAndNavigate = async (overrideDestId = null) => {
    setMappingDialogOpen(false);

    const updatedConfig = processWorkflowDatas;
    const nodes = updatedConfig?.nodes || [];
    const edges = updatedConfig?.edges || [];

    const { sourceId, dest1Id, source, dest1, dest2 } = mappingDialogData || {};

    const allConfigs =
      nodes
        ?.map((node) => {
          const connId = node.config?.connection?.conn_id || '';
          const mod = node.config?.module || '';
          const nodeKey = `${node.id}-${connId}-${mod}`;
          const isSource = node.type === 'sourceNode' || node.type === 'source';
          const isDestination =
            node.type === 'destinationNode' || node.type === 'destination';
          if (!(isSource || isDestination)) return null;
          return {
            id: node.id,
            label: node.config?.label,
            connection: node.config?.connection,
            module: node.config?.module,
            selectedEntities: isSource
              ? selectedSourceEntitiesByWorkflow[nodeKey] || []
              : selectedDestinationEntitiesByWorkflow[nodeKey] || [],
            type: isSource ? 'source' : 'destination',
          };
        })
        .filter(Boolean) || [];

    const resolvedSourceId =
      sourceId || allConfigs.find((c) => c.type === 'source')?.id || null;

    // --- decision-aware discovery first
    const decisionNode = findDecisionNode(nodes);
    let decisionMeta = null;
    let nextDestId = null;
    let destIds = [];
    let destNames = [];

    if (decisionNode) {
      const destNodes = edges
        .filter((e) => e.source === decisionNode.id)
        .map((e) => nodes.find((n) => n.id === e.target))
        .filter(isDestNode);

      destIds = destNodes.map((d) => d.id);
      destNames = destNodes.map((d) => d?.config?.label || d?.label || '');
    }

    // --- resolve target: number -> index into destIds; string -> explicit id; else fallback
    const targetDestId =
      typeof overrideDestId === 'number'
        ? (destIds[overrideDestId] ?? dest1Id ?? destIds[0] ?? null)
        : typeof overrideDestId === 'string'
          ? overrideDestId
          : (dest1Id ?? destIds[0] ?? null);

    const filtered = allConfigs.filter((c) => {
      if (c.type === 'source')
        return resolvedSourceId ? c.id === resolvedSourceId : true;
      if (c.type === 'destination') return c.id === targetDestId;
      return false;
    });

    const srcEntities = filtered
      .filter((c) => c.type === 'source')
      .flatMap((c) => c.selectedEntities || []);
    const dstEntities = filtered
      .filter((c) => c.type === 'destination')
      .flatMap((c) => c.selectedEntities || []);

    // --- decision meta (or legacy fallback when no decision node)
    if (decisionNode && destIds.length) {
      const baseDest = targetDestId;
      const currentIndex = Math.max(0, destIds.indexOf(baseDest));
      nextDestId = destIds[currentIndex + 1] ?? null;

      decisionMeta = {
        decisionNodeId: decisionNode.id,
        sourceId: resolvedSourceId,
        destIds,
        destNames,
        currentIndex,
      };
    } else {
      // ✅ Legacy fallback (no decision node): preserves behavior needed for source dropdown
      const destNodes = nodes.filter(isDestNode);
      if (destNodes.length > 1) {
        const dIds = destNodes.map((d) => d.id);
        const dNames = destNodes.map((d) => d?.config?.label || d?.label || '');
        decisionMeta = {
          decisionNodeId: decisionNode?.id || null,
          sourceId: resolvedSourceId,
          destIds: dIds,
          destNames: dNames,
          currentIndex: Math.max(0, dIds.indexOf(targetDestId)),
        };
        nextDestId = dIds[decisionMeta.currentIndex + 1] || null;
      }
    }

    const canGoNext = !!nextDestId;

    navigate(`/parentworkflow/${workflowId || 'defaultWorkflow'}/mapping`, {
      state: {
        finalConfigPayload: filtered,
        selectedSourceEntities: srcEntities,
        selectedDestinationEntities: dstEntities,
        canGoNext,
        primaryDestinationId: targetDestId,
        secondaryDestinationId: nextDestId,
        decisionMeta,

        sourceName: source,
        dest1Name: dest1,
        dest2Name: dest2,
      },
    });
  };

  useEffect(() => {
    const st = location.state || {};
    const proceed = st.proceedNextMapping || st.proceedSecondMapping;
    let meta = st.decisionMeta;
    const legacyNext = st.secondaryDestinationId;

    if (!proceed) return;

    if (!meta || !Array.isArray(meta.destIds) || meta.destIds.length === 0) {
      const nodes = processWorkflowDatas?.nodes || [];
      const edges = processWorkflowDatas?.edges || [];
      const decisionNode = findDecisionNode(nodes);
      if (!decisionNode) return;
      if (decisionNode) {
        const destNodes = edges
          .filter((e) => e.source === decisionNode.id)
          .map((e) => nodes.find((n) => n.id === e.target))
          .filter(isDestNode);
        const destIds = destNodes.map((d) => d.id);
        const destNames = destNodes.map(
          (d) => d?.config?.label || d?.label || '',
        );
        meta = {
          decisionNodeId: decisionNode.id,
          sourceId: nodes.find(isSourceNode)?.id || null,
          destIds,
          destNames,
          currentIndex: Math.max(0, destIds.indexOf(legacyNext)) || 0,
        };
      }
    }

    if (meta?.destIds?.length) {
      const overrideDestId = meta.destIds[meta.currentIndex] || null;
      if (overrideDestId) {
        navigate(location.pathname, { replace: true, state: {} });
        handleProceedAndNavigate(overrideDestId);
      }
    } else if (legacyNext) {
      navigate(location.pathname, { replace: true, state: {} });
      handleProceedAndNavigate(legacyNext);
    }
    // eslint-disable-next-line
  }, [location.key]);

  //----------------------------------------------
  useEffect(() => {
    if (workflowId) {
      dispatch(setNodes([]));
      dispatch(setEdges([]));
      if (workflows[workflowId]) {
        dispatch(loadWorkflow({ workflowId: workflowId }));
      }
    }
  }, [workflowId, workflows, dispatch]);

  //   useEffect(() => {
  //   if (workflowname && !workflows[workflowname]) {
  //     // Workflow missing in Redux: fetch from API
  //     dispatch(fetchWorkflowConfigAndSet({ workflowId: workflowname, workflowName: workflowname }));
  //   } else if (workflowname && workflows[workflowname]) {
  //     // Workflow in Redux: just load it into nodes/edges
  //     dispatch(loadWorkflow({ name: workflowname }));
  //   }
  // }, [workflowname, workflows, dispatch]);

  useEffect(() => {
    const loadedWorkflow = workflows[workflowId];

    console.log('loaded workflow:', loadedWorkflow);

    // Load feature flags from backend config
    if (loadedWorkflow?.workflowJson?.featureFlags) {
      setEnableBacktrack(
        loadedWorkflow.workflowJson.featureFlags.enableBacktrack || false,
      );
    } else if (loadedWorkflow?.config?.featureFlags) {
      setEnableBacktrack(
        loadedWorkflow.config.featureFlags.enableBacktrack || false,
      );
    } else if (loadedWorkflow?.featureFlags) {
      setEnableBacktrack(loadedWorkflow.featureFlags.enableBacktrack || false);
    }

    if (
      loadedWorkflow?.workflowJson &&
      !nodes.length &&
      !edges.length &&
      (loadedWorkflow.workflowJson.nodes?.length ||
        loadedWorkflow.workflowJson.edges?.length)
    ) {
      console.log('Workflow Restoration Block TRIGGERED::');
      const savedNodes = loadedWorkflow.workflowJson.nodes || [];
      const savedEdges = loadedWorkflow.workflowJson.edges || [];

      if (savedNodes.length) {
        dispatch(setNodes(savedNodes));
      }
      if (savedEdges.length) {
        dispatch(setEdges(savedEdges));
      }
    }
  }, [workflowId, workflows, dispatch]);

  useEffect(() => {
    if (!nodes.length || !edges.length) return;

    // const hasConfig = (n) => {
    //   console.log('hasconfig n:::', n);
    //   const c = n?.data?.config || {};
    //   const lbl = (n?.data?.label || '').toLowerCase();
    //   const isJira = lbl.includes('jira');
    //   if (!c.connection?.conn_id) return false;
    //   if (isJira) return !!c.project?.id || !!c.project?.key;
    //   return !!c.module;
    // };
    const hasConfig = (n) => {
      console.log('hasconfig n:::', n);

      const c = n?.data?.config || {};
      const lbl = (n?.data?.label || '').toLowerCase();

      const isJira = lbl.includes('jira');

      const isSqlLike = lbl.includes('sql') || lbl.includes('postgres');

      if (!c.connection?.conn_id) return false;

      if (isJira) {
        return !!c.project?.id || !!c.project?.key;
      }
      if (isSqlLike) {
        return true;
      }
      return !!c.module;
    };

    const enhancedEdges = edges.map((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);

      const sourceOk = hasConfig(sourceNode);
      const targetOk = hasConfig(targetNode);
      const useSideAware = !sourceOk || !targetOk;

      const isIntegrationNode =
        sourceNode?.type === 'integrationNode' ||
        targetNode?.type === 'integrationNode';

      const isJiraLabel =
        (sourceNode?.data?.label || '').toLowerCase().trim() ===
          'jira (integretion)' ||
        (targetNode?.data?.label || '').toLowerCase().trim() ===
          'jira (integretion)';

      const isLShape = isIntegrationNode && isJiraLabel;

      // Check if edge is connected to a backtrack node
      const isBacktrackEdge =
        sourceNode?.type === 'backtrackNode' ||
        targetNode?.type === 'backtrackNode';

      // Use purple for backtrack edges, otherwise use default blue
      const edgeColor = isBacktrackEdge
        ? '#9c27b6'
        : darkMode
          ? '#3a8eff'
          : '#1976d2';

      return {
        ...edge,
        // type: isLShape ? 'lshape' : 'default',
        type: useSideAware ? 'sideaware' : isLShape ? 'lshape' : 'default',
        data: { sourceOk, targetOk },
        animated: !isLShape,
        markerEnd: {
          type: 'arrowclosed',
          width: 20,
          height: 20,
          color: edge.markerEnd?.color || edgeColor,
        },
        style: isBacktrackEdge
          ? { stroke: '#9c27b6', strokeWidth: 2, ...edge.style }
          : edge.style,
      };
    });

    if (JSON.stringify(enhancedEdges) !== JSON.stringify(edges)) {
      dispatch(setEdges(enhancedEdges));
    }
  }, [nodes, edges, darkMode, dispatch]);

  // Helper function to normalize entity selection keys
  // Matches saved keys (which may have empty module) with actual node keys
  const normalizeEntityKey = useCallback((savedKey, nodes, targetType) => {
    if (!savedKey || !nodes || !Array.isArray(nodes)) return savedKey;

    // Filter nodes by target type
    const allNodes = nodes.filter(
      (n) =>
        (targetType === 'source' &&
          (n.type === 'sourceNode' || n.type === 'source')) ||
        (targetType === 'destination' &&
          (n.type === 'destinationNode' || n.type === 'destination')),
    );

    // Try to match by connId first (most reliable since it's a UUID)
    // The saved key format is: "nodeId-connId-module" or "nodeId-connId-"
    for (const node of allNodes) {
      const nodeConnId = node?.config?.connection?.conn_id || '';
      const nodeId = node?.id || '';
      if (!nodeConnId || !nodeId) continue;

      // Build the expected key patterns
      const nodeModule = node?.config?.module || '';
      // Normalize SQL module to lowercase
      const normalizedModule =
        nodeModule && nodeModule.toLowerCase() === 'sql' ? 'sql' : nodeModule;

      const keyWithModule = `${nodeId}-${nodeConnId}-${normalizedModule}`;
      const keyWithoutModule = `${nodeId}-${nodeConnId}-`;

      // Check if saved key matches either pattern
      if (savedKey === keyWithModule) {
        return savedKey; // Already correct
      }

      // Check if saved key matches the pattern without module (ends with "-")
      // and starts with nodeId-connId-
      if (
        savedKey === keyWithoutModule ||
        (savedKey.endsWith('-') &&
          savedKey.startsWith(`${nodeId}-${nodeConnId}-`))
      ) {
        // Saved key has empty module, return key with module
        return keyWithModule;
      }
    }

    return savedKey; // Return original if no match found
  }, []);

  useEffect(() => {
    const loadedWorkflow = workflows[workflowId];

    // Also restore connectionDetails if available
    if (loadedWorkflow?.connectionDetails?.length) {
      console.log(
        '🔄 Restoring connectionDetails:',
        loadedWorkflow.connectionDetails.length,
      );
    }

    // if(loadedWorkflow?.workflowId){
    //   setLastWorkflowId(loadedWorkflow.workflowId);
    // }

    if (loadedWorkflow?.config?.entitySelections && nodes.length > 0) {
      const { source = {}, destination = {} } =
        loadedWorkflow.config.entitySelections;

      // Normalize and set source entity selections
      Object.entries(source).forEach(([savedKey, entities]) => {
        const normalizedKey = normalizeEntityKey(savedKey, nodes, 'source');
        const existing = selectedSourceEntitiesByWorkflow[normalizedKey] || [];

        // Only set if not already set or if empty
        if (!existing || existing.length === 0) {
          console.log(
            `🔄 Normalizing source key: "${savedKey}" -> "${normalizedKey}"`,
          );
          dispatch(
            setConfirmedSourceEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
          dispatch(
            setSelectedSourceEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
        }
      });

      // Normalize and set destination entity selections
      Object.entries(destination).forEach(([savedKey, entities]) => {
        const normalizedKey = normalizeEntityKey(
          savedKey,
          nodes,
          'destination',
        );
        const existing =
          selectedDestinationEntitiesByWorkflow[normalizedKey] || [];

        // Only set if not already set or if empty
        if (!existing || existing.length === 0) {
          console.log(
            `🔄 Normalizing destination key: "${savedKey}" -> "${normalizedKey}"`,
          );
          dispatch(
            setConfirmedDestinationEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
          dispatch(
            setSelectedDestinationEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
        }
      });
    }
  }, [
    workflowId,
    workflows,
    nodes,
    dispatch,
    normalizeEntityKey,
    selectedSourceEntitiesByWorkflow,
    selectedDestinationEntitiesByWorkflow,
  ]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!workflowId || !hasMounted.current) return;

    const timeout = setTimeout(() => {
      const wfId = workflowId;

      dispatch(
        saveWorkflow({
          workflowId: wfId,
          nodes,
          edges,
          connectionDetails,
          // ...(workflowId && { workflowId }),
          ...(workflowConfigurationId && { workflowConfigurationId }),
          ...(workflowRunId && { workflowRunId }),
        }),
      );
    }, 800);

    return () => clearTimeout(timeout);
  }, [
    nodes,
    edges,
    workflowId,
    connectionDetails,
    dispatch,
    // workflowId,
    // workflowConfigurationId,
  ]);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  // Handle validation for single source + multiple destinations scenario
  useEffect(() => {
    if (!selectedNode || selectedNode.type !== 'integrationNode') {
      singleSourceMultiDestToastShownRef.current = false;
      return;
    }

    const integrationNodeId = selectedNode.id;
    const isDecisionNode =
      /^decision-/i.test(selectedNode?.id) ||
      /^decision/i.test(selectedNode?.data?.label || '');

    // Skip validation for decision nodes
    if (isDecisionNode) {
      singleSourceMultiDestToastShownRef.current = false;
      return;
    }

    const sources = nodes.filter((n) => n.type === 'sourceNode');
    const dests = nodes.filter((n) => n.type === 'destinationNode');

    const isSqlLike = (n) => {
      const lbl = (n?.data?.label || '').toLowerCase();
      return lbl.includes('sql') || lbl.includes('postgres');
    };

    const allSrcConfigured = sources.every((sn) => {
      const cfg = sn.data?.config || {};
      if (!cfg.connection) return false;
      if (isSqlLike(sn)) return true;
      return !!cfg.module;
    });

    const allDestConfigured = dests.every((dn) => {
      const cfg = dn.data?.config || {};
      if (!cfg.connection) return false;
      if (isSqlLike(dn)) return true;
      return !!cfg.module;
    });

    const allSrcEdges = sources.every((sn) =>
      edges.some((e) => e.source === sn.id && e.target === integrationNodeId),
    );
    const allDestEdges = dests.every((dn) =>
      edges.some((e) => e.source === integrationNodeId && e.target === dn.id),
    );

    // Check connected sources and destinations for this integration node
    const connectedSources = sources.filter((sn) =>
      edges.some((e) => e.source === sn.id && e.target === integrationNodeId),
    );
    const connectedDests = dests.filter((dn) =>
      edges.some((e) => e.source === integrationNodeId && e.target === dn.id),
    );

    // Validation: Single source + multiple destinations + all configured
    const isSingleSourceMultipleDest =
      connectedSources.length === 1 &&
      connectedDests.length > 1 &&
      allSrcConfigured &&
      allDestConfigured &&
      allSrcEdges &&
      allDestEdges;

    // Show toast only once when condition becomes true
    if (
      isSingleSourceMultipleDest &&
      !singleSourceMultiDestToastShownRef.current
    ) {
      singleSourceMultiDestToastShownRef.current = true;
      toast.error(
        'Please use decision node for single source multiple destinations.',
        {
          autoClose: 5000,
        },
      );
    } else if (!isSingleSourceMultipleDest) {
      // Reset when condition is no longer met
      singleSourceMultiDestToastShownRef.current = false;
    }
  }, [selectedNode, nodes, edges]);

  const onNodesChange = useCallback(
    (changes) => dispatch(setNodes(applyNodeChanges(changes, nodes))),
    [nodes, dispatch],
  );

  const onEdgesChange = useCallback(
    (changes) => dispatch(setEdges(applyEdgeChanges(changes, edges))),
    [edges, dispatch],
  );

  // const onConnect = useCallback(
  //   (params) => {
  //     const newEdge = {
  //       ...params,
  //       animated: true,
  //       markerEnd: {
  //         type: 'arrowclosed',
  //         width: 20,
  //         height: 20,
  //         color: darkMode ? '#3a8eff' : '#1976d2',
  //       },
  //     };
  //     dispatch(setEdges(addEdge(newEdge, edges)));
  //   },
  //   [edges, dispatch, darkMode]
  // );

  // const onConnect = useCallback(
  //   (params) => {
  //     const sourceNode = nodes.find((n) => n.id === params.source);
  //     const destinationNode = nodes.find((n) => n.id === params.target);

  //     const isIntegrationNode =
  //       sourceNode?.type === 'integrationNode' ||
  //       destinationNode?.type === 'integrationNode';
  //     const isJiraLabel =
  //       sourceNode?.data?.label?.toLowerCase() === 'jira (integretion)' ||
  //       destinationNode?.data?.label?.toLowerCase() === 'jira (integretion)';

  //     const isLShape = isIntegrationNode && isJiraLabel;

  //     const newEdge = {
  //       ...params,
  //       animated: !isLShape,
  //       type: isLShape ? 'lshape' : 'default',
  //       markerEnd: {
  //         type: 'arrowclosed',
  //         width: 20,
  //         height: 20,
  //         color: darkMode ? '#3a8eff' : '#1976d2',
  //       },
  //     };

  //     dispatch(setEdges(addEdge(newEdge, edges)));
  //   },
  //   [edges, dispatch, nodes, darkMode]
  // );

  const onConnect = useCallback(
    (params) => {
      const { source, target } = params;

      const sourceNode = nodes.find((n) => n.id === source);
      const targetNode = nodes.find((n) => n.id === target);

      console.log('CONNECT ATTEMPT', {
        source,
        target,
        sourceType: sourceNode?.type,
        targetType: targetNode?.type,
      });

      const isIntegration = (n) => n?.type === 'integrationNode';
      const isDecisionIntegration = (n) =>
        isIntegration(n) &&
        (/^decision-/i.test(n?.id) || /^decision/i.test(n?.data?.label));

      if (!sourceNode || !targetNode) return;

      // if (
      //   (isIntegration(sourceNode) && targetNode.type === 'destinationNode') ||
      //   (isIntegration(targetNode) && sourceNode.type === 'destinationNode')
      // ) {
      //   const integrationNode = isIntegration(sourceNode)
      //     ? sourceNode
      //     : targetNode;

      //   if (!isDecisionIntegration(integrationNode)) {
      //     const integrationId = integrationNode.id;
      //     const destinationAlreadyConnected = edges.some(
      //       (e) =>
      //         (e.source === integrationId &&
      //           nodes.find((n) => n.id === e.target)?.type ===
      //             'destinationNode') ||
      //         (e.target === integrationId &&
      //           nodes.find((n) => n.id === e.source)?.type ===
      //             'destinationNode'),
      //     );

      //     if (destinationAlreadyConnected) {
      //       toast.error(
      //         'Only 1 destination node can connect to an field mapping node',
      //       );
      //       return;
      //     }
      //   }
      // }

      const isTargetIntegration = isIntegration(targetNode);
      const isSourceNode = sourceNode.type === 'sourceNode';

      if (isTargetIntegration) {
        const decision = isDecisionIntegration(targetNode);
        const connectedEdges = edges.filter((e) => e.target === target);

        const sourceConnections = connectedEdges.filter((e) => {
          const node = nodes.find((n) => n.id === e.source);
          return node?.type === 'sourceNode';
        });

        if (isSourceNode && decision && sourceConnections.length >= 1) {
          toast.error('Decision node accepts only 1 source');
          return;
        }
        if (isSourceNode && !decision && sourceConnections.length >= 3) {
          toast.error('Max 3 source nodes can connect to an integration node');
          return;
        }
      }

      if (targetNode.type === 'destinationNode') {
        const hasInput = edges.some((e) => e.target === target);
        if (hasInput) {
          toast.error('Destination node can only accept 1 connection');
          return;
        }
      }

      // Backtrack node validation: only 1 backtrack node allowed
      if (
        targetNode.type === 'backtrackNode' ||
        sourceNode.type === 'backtrackNode'
      ) {
        const existingBacktrackNodes = nodes.filter(
          (n) => n.type === 'backtrackNode',
        );
        if (
          existingBacktrackNodes.length > 0 &&
          !existingBacktrackNodes.some(
            (n) => n.id === source || n.id === target,
          )
        ) {
          toast.error('Only 1 backtrack node is allowed per workflow');
          return;
        }
      }

      // Backtrack connection rules
      // if (sourceNode.type === 'backtrackNode') {
      //   // Backtrack can only connect to source nodes
      //   if (targetNode.type !== 'sourceNode') {
      //     toast.error('Backtrack node can only connect to source nodes');
      //     return;
      //   }
      //   // Backtrack can only have 1 output
      //   const hasOutput = edges.some((e) => e.source === source);
      //   if (hasOutput) {
      //     toast.error('Backtrack node can only have 1 output connection');
      //     return;
      //   }
      // }

      // if (targetNode.type === 'backtrackNode') {
      //   // Only destination nodes can connect to backtrack
      //   if (sourceNode.type !== 'destinationNode') {
      //     toast.error('Backtrack node can only accept connections from destination nodes');
      //     return;
      //   }
      //   // Backtrack can only have 1 input
      //   const hasInput = edges.some((e) => e.target === target);
      //   if (hasInput) {
      //     toast.error('Backtrack node can only accept 1 input connection');
      //     return;
      //   }
      // }

      // ✔ Backtrack connection rules: allow MULTIPLE inputs & outputs
      if (sourceNode.type === 'backtrackNode') {
        // Backtrack → can connect to source nodes only
        if (targetNode.type !== 'sourceNode') {
          toast.error('Backtrack node can only connect to source nodes');
          return;
        }
        // No output limit
      }

      if (targetNode.type === 'backtrackNode') {
        // Only destination nodes can connect to backtrack
        if (sourceNode.type !== 'destinationNode') {
          toast.error(
            'Backtrack node can only accept connections from destination nodes',
          );
          return;
        }
        // No input limit
      }

      // Check if edge is connected to a backtrack node
      const isBacktrackEdge =
        sourceNode.type === 'backtrackNode' ||
        targetNode.type === 'backtrackNode';

      // Use purple for backtrack edges, otherwise use default blue
      const edgeColor = isBacktrackEdge
        ? '#9c27b6'
        : darkMode
          ? '#3a8eff'
          : '#1976d2';

      const newEdge = {
        ...params,
        animated: true,
        markerEnd: {
          type: 'arrowclosed',
          width: 20,
          height: 20,
          color: edgeColor,
        },
        ...(isBacktrackEdge && {
          style: { stroke: '#9c27b6', strokeWidth: 2 },
        }),
      };
      dispatch(setEdges(addEdge(newEdge, edges)));
    },
    [nodes, edges, dispatch, darkMode],
  );

  const onEdgeClick = useCallback(
    (event, edge) => {
      const updatedEdges = edges.filter((e) => e.id !== edge.id);
      dispatch(setEdges(updatedEdges));
    },
    [edges, dispatch],
  );

  // working code of single node drag for source and destinaion

  // const onDrop = useCallback(
  //   (event) => {
  //     event.preventDefault();

  //     const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
  //     const type = event.dataTransfer.getData('application/reactflow');
  //     const nodeType = event.dataTransfer.getData('nodeType');

  //     if (!type || !nodeType) return;

  //     const centerPosition = {
  //       x: reactFlowBounds.width / 2,
  //       y: reactFlowBounds.height / 3,
  //     };

  //     // Offset every new node slightly so they don’t overlap
  //     const offsetStep = 50;
  //     const offsetX = (nodes.length % 7) * offsetStep;
  //     const offsetY = Math.floor(nodes.length / 5) * offsetStep;

  //     const dropPosition = reactFlowInstance.current.screenToFlowPosition({
  //       x: centerPosition.x + offsetX,
  //       y: centerPosition.y + offsetY,
  //     });

  //     const nodeTypesMap = {
  //       Source: 'sourceNode',
  //       Destination: 'destinationNode',
  //       Transformation: 'transformationNode',
  //       Processor: 'processorNode',
  //       Integretion: 'integrationNode',
  //     };

  //     const newNode = {
  //       id: `${type}-${uuidv4()}`,
  //       type: nodeTypesMap[nodeType] || 'default',
  //       position: dropPosition,
  //       data: {
  //         label: `${type} (${nodeType})`,
  //         config:
  //           nodeTypesMap[nodeType] === 'integrationNode'
  //             ? {
  //                 id: `${type}-${uuidv4()}`,
  //                 label: `${type} (${nodeType})`,
  //                 type: 'integrationNode',
  //               }
  //             : '',
  //       },
  //     };

  //     dispatch(setNodes([...nodes, newNode]));
  //   },
  //   [nodes, dispatch]
  // );

  ////////////////////////////////////////////////////////

  // multiple nodes

  // const onDrop = useCallback(
  //   (event) => {
  //     event.preventDefault();

  //     const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
  //     const multiData = event.dataTransfer.getData('multi-nodes');
  //     const nodeType = event.dataTransfer.getData('nodeType');
  //     const type = event.dataTransfer.getData('application/reactflow');

  //     const nodeTypesMap = {
  //       Source: 'sourceNode',
  //       Destination: 'destinationNode',
  //       Transformation: 'transformationNode',
  //       Processor: 'processorNode',
  //       Integretion: 'integrationNode',
  //     };

  //     const getConfig = (type) => {
  //       return type === 'integrationNode' ? { custom: {} } : '';
  //     };

  //     if (multiData) {
  //       const items = JSON.parse(multiData);
  //       const newNodes = [];
  //       const newEdges = [];

  //       const dropBase = reactFlowInstance.current.screenToFlowPosition({
  //         x: reactFlowBounds.width / 2,
  //         y: reactFlowBounds.height / 3,
  //       });

  //       const idMap = {
  //         sourceNode: [],
  //         destinationNode: [],
  //       };

  //       items.forEach((item, index) => {
  //         const typeKey = item.type || item.sourceType || 'Source';
  //         const nodeTypeKey = nodeTypesMap[typeKey] || 'sourceNode';
  //         const id = `${item.name}-${uuidv4()}`;

  //         const position = {
  //           x: dropBase.x,
  //           y: dropBase.y + index * 150,
  //         };

  //         const node = {
  //           id,
  //           type: nodeTypeKey,
  //           position,
  //           data: {
  //             label: `${item.name} (${typeKey})`,
  //             config: getConfig(nodeTypeKey),
  //           },
  //         };

  //         newNodes.push(node);
  //         if (!idMap[nodeTypeKey]) idMap[nodeTypeKey] = [];
  //         idMap[nodeTypeKey].push(id);
  //       });

  //       const integrationNodes = nodes.filter(
  //         (n) => n.type === 'integrationNode'
  //       );

  //       // working code

  //       // integrationNodes.forEach((integrationNode) => {
  //       //   idMap.sourceNode.forEach((srcId) => {
  //       //     newEdges.push({
  //       //       id: `e-${srcId}-${integrationNode.id}`,
  //       //       source: srcId,
  //       //       target: integrationNode.id,
  //       //       type: 'default',
  //       //     });
  //       //   });

  //       //   idMap.destinationNode.forEach((destId) => {
  //       //     newEdges.push({
  //       //       id: `e-${integrationNode.id}-${destId}`,
  //       //       source: integrationNode.id,
  //       //       target: destId,
  //       //       type: 'default',
  //       //     });
  //       //   });
  //       // });

  //       ///////////////

  //       const isDecisionIntegration = (n) =>
  //         n.type === 'integrationNode' &&
  //         (/^decision-/i.test(n.id) || /^decision/i.test(n?.data?.label || ''));

  //       integrationNodes.forEach((integrationNode) => {
  //         const integrationId = integrationNode.id;
  //         const decision = isDecisionIntegration(integrationNode);

  //         const existingSrc = edges
  //           .filter((e) => e.target === integrationId)
  //           .filter(
  //             (e) => nodes.find((n) => n.id === e.source)?.type === 'sourceNode'
  //           ).length;

  //         let addedSrc = 0;
  //         const addSrc = (srcId) => {
  //           if (
  //             edges.some(
  //               (e) => e.source === srcId && e.target === integrationId
  //             )
  //           )
  //             return;
  //           if (
  //             newEdges.some(
  //               (e) => e.source === srcId && e.target === integrationId
  //             )
  //           )
  //             return;
  //           newEdges.push({
  //             id: `e-${srcId}-${integrationId}`,
  //             source: srcId,
  //             target: integrationId,
  //             type: 'default',
  //           });
  //         };

  //         if (decision) {
  //           for (const srcId of idMap.sourceNode) {
  //             if (existingSrc + addedSrc >= 1) break;
  //             addSrc(srcId);
  //             addedSrc += 1;
  //           }
  //         } else {
  //           const CAP = 3;
  //           for (const srcId of idMap.sourceNode) {
  //             if (existingSrc + addedSrc >= CAP) break;
  //             addSrc(srcId);
  //             addedSrc += 1;
  //           }
  //         }

  //         for (const destId of idMap.destinationNode) {
  //           if (edges.some((e) => e.target === destId)) continue;
  //           if (newEdges.some((e) => e.target === destId)) continue;
  //           newEdges.push({
  //             id: `e-${integrationId}-${destId}`,
  //             source: integrationId,
  //             target: destId,
  //             type: 'default',
  //           });
  //         }
  //       });

  //       dispatch(setNodes([...nodes, ...newNodes]));
  //       dispatch(setEdges([...edges, ...newEdges]));
  //       setClearSourceSelection((p) => p + 1);
  //       return;
  //     }

  //     if (!type || !nodeType) return;

  //     const centerPosition = {
  //       x: reactFlowBounds.width / 2,
  //       y: reactFlowBounds.height / 3,
  //     };

  //     const offsetStep = 50;
  //     const offsetX = (nodes.length % 7) * offsetStep;
  //     const offsetY = Math.floor(nodes.length / 5) * offsetStep;

  //     const dropPosition = reactFlowInstance.current.screenToFlowPosition({
  //       x: centerPosition.x + offsetX,
  //       y: centerPosition.y + offsetY,
  //     });

  //     const nodeId = `${type}-${uuidv4()}`;
  //     const nodeTypeKey = nodeTypesMap[nodeType] || 'default';

  //     const newNode = {
  //       id: nodeId,
  //       type: nodeTypeKey,
  //       position: dropPosition,
  //       data: {
  //         label: `${type} (${nodeType})`,
  //         // config:
  //         //   nodeTypesMap[nodeType] === 'integrationNode'
  //         //     ? {
  //         //         id: `${type}-${uuidv4()}`,
  //         //         label: `${type} (${nodeType})`,
  //         //         type: 'integrationNode',
  //         //       }
  //         //     : '',
  //         config: getConfig(nodeTypeKey),
  //       },
  //     };

  //     dispatch(setNodes([...nodes, newNode]));

  //     setClearSourceSelection((p) => p + 1);
  //   },
  //   [nodes, edges, dispatch, reactFlowInstance]
  // );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const multiData = event.dataTransfer.getData('multi-nodes');
      const nodeType = event.dataTransfer.getData('nodeType');
      const type = event.dataTransfer.getData('application/reactflow');

      const nodeTypesMap = {
        Source: 'sourceNode',
        Destination: 'destinationNode',
        Transformation: 'transformationNode',
        Processor: 'processorNode',
        Integretion: 'integrationNode',
        Backtrack: 'backtrackNode',
      };

      const getConfig = (t) => {
        if (t === 'integrationNode') return { custom: {} };
        if (t === 'backtrackNode') return { update_operation: 'upsert' };
        return '';
      };

      const centerX = reactFlowBounds.width / 2;
      const centerY = reactFlowBounds.height / 3;
      const base = reactFlowInstance.current.screenToFlowPosition({
        x: centerX,
        y: centerY,
      });

      const integrationNodes = nodes.filter(
        (n) => n.type === 'integrationNode',
      );
      const getPos = (n) => ({
        x: n.positionAbsolute?.x ?? n.position?.x ?? 0,
        y: n.positionAbsolute?.y ?? n.position?.y ?? 0,
      });
      let anchor = base;
      if (integrationNodes.length) {
        let best = null,
          bestD2 = Infinity;
        for (const n of integrationNodes) {
          const p = getPos(n);
          const d2 =
            (p.x - base.x) * (p.x - base.x) + (p.y - base.y) * (p.y - base.y);
          if (d2 < bestD2) {
            bestD2 = d2;
            best = p;
          }
        }
        if (best) anchor = best;
      }

      const LANE_GAP = 280;
      const laneX = (t) => {
        if (t === 'sourceNode') return anchor.x - LANE_GAP;
        if (t === 'destinationNode') return anchor.x + LANE_GAP;
        if (t === 'backtrackNode') return anchor.x + LANE_GAP / 2;
        return anchor.x;
      };

      if (multiData) {
        const items = JSON.parse(multiData);
        const newNodes = [];
        const newEdges = [];
        const idMap = { sourceNode: [], destinationNode: [] };
        const rowCounters = {};

        items.forEach((item) => {
          const typeKey = item.type || item.sourceType || 'Source';
          const nodeTypeKey = nodeTypesMap[typeKey] || 'sourceNode';
          const id = `${item.name}-${uuidv4()}`;

          const row = rowCounters[nodeTypeKey] ?? 0;
          rowCounters[nodeTypeKey] = row + 1;

          newNodes.push({
            id,
            type: nodeTypeKey,
            position: { x: laneX(nodeTypeKey), y: anchor.y + row * 150 },
            data: {
              label: `${item.name} (${typeKey})`,
              config: getConfig(nodeTypeKey),
            },
          });

          if (!idMap[nodeTypeKey]) idMap[nodeTypeKey] = [];
          idMap[nodeTypeKey].push(id);
        });

        const isDecisionIntegration = (n) =>
          n.type === 'integrationNode' &&
          (/^decision-/i.test(n.id) || /^decision/i.test(n?.data?.label || ''));

        integrationNodes.forEach((integrationNode) => {
          const integrationId = integrationNode.id;
          const decision = isDecisionIntegration(integrationNode);

          const existingSrc = edges
            .filter((e) => e.target === integrationId)
            .filter(
              (e) =>
                nodes.find((n) => n.id === e.source)?.type === 'sourceNode',
            ).length;

          let addedSrc = 0;
          const addSrc = (srcId) => {
            if (
              edges.some(
                (e) => e.source === srcId && e.target === integrationId,
              )
            )
              return;
            if (
              newEdges.some(
                (e) => e.source === srcId && e.target === integrationId,
              )
            )
              return;
            newEdges.push({
              id: `e-${srcId}-${integrationId}`,
              source: srcId,
              target: integrationId,
              type: 'default',
            });
          };

          if (decision) {
            for (const srcId of idMap.sourceNode) {
              if (existingSrc + addedSrc >= 1) break;
              addSrc(srcId);
              addedSrc += 1;
            }
          } else {
            const CAP = 3;
            for (const srcId of idMap.sourceNode) {
              if (existingSrc + addedSrc >= CAP) break;
              addSrc(srcId);
              addedSrc += 1;
            }
          }

          const existingDest = edges
            .filter((e) => e.source === integrationId)
            .filter((e) => {
              const targetNode = nodes.find((n) => n.id === e.target);
              return (
                targetNode?.type === 'destinationNode' ||
                targetNode?.type === 'destination'
              );
            }).length;

          let addedDest = 0;

          for (const destId of idMap.destinationNode) {
            if (!decision && existingDest + addedDest >= 3) break;
            if (edges.some((e) => e.target === destId)) continue;
            if (newEdges.some((e) => e.target === destId)) continue;
            newEdges.push({
              id: `e-${integrationId}-${destId}`,
              source: integrationId,
              target: destId,
              type: 'default',
            });
            addedDest += 1;
          }
        });

        dispatch(setNodes([...nodes, ...newNodes]));
        dispatch(setEdges([...edges, ...newEdges]));
        setClearSourceSelection((p) => p + 1);
        return;
      }

      if (!type || !nodeType) return;

      // Handle Backtrack specially - it can come with nodeType 'Integretion' but type 'Backtrack'
      const nodeTypeKey =
        type === 'Backtrack'
          ? nodeTypesMap['Backtrack']
          : nodeTypesMap[nodeType] || 'default';
      // ✅ BLOCK backtrack drag when no connectionDetails JSON exists
      if (nodeTypeKey === 'backtrackNode') {
        const hasConnectionJson =
          Array.isArray(connectionDetails) && connectionDetails.length > 0;

        if (!hasConnectionJson) {
          toast.error(
            'Backtrack node can be added only after you configure at least one mapping/connection.',
          );
          return;
        }

        // Optional: also enforce max 1 backtrack node at drag-time
        const existingBacktrack = nodes.some((n) => n.type === 'backtrackNode');
        if (existingBacktrack) {
          toast.error('Only 1 backtrack node is allowed per workflow');
          return;
        }
      }
      const offsetY = Math.floor(nodes.length / 5) * 50;

      const newNode = {
        id: `${type}-${uuidv4()}`,
        type: nodeTypeKey,
        position: { x: laneX(nodeTypeKey), y: anchor.y + offsetY },
        data: {
          label: `${type} (${nodeType})`,
          config: getConfig(nodeTypeKey),
        },
      };

      // Auto-connect backtrack node and enable feature flag
      const newEdges = [];
      if (nodeTypeKey === 'backtrackNode') {
        // Auto-enable feature flag when backtrack node is added
        setEnableBacktrack(true);

        // Find the last destination node
        const destinationNodes = nodes.filter(
          (n) => n.type === 'destinationNode' || n.type === 'destination',
        );
        // const lastDestination = destinationNodes[destinationNodes.length - 1];

        // Find the first source node
        const sourceNodes = nodes.filter(
          (n) => n.type === 'sourceNode' || n.type === 'source',
        );
        // const firstSource = sourceNodes[0];

        destinationNodes.forEach((dest) => {
          newEdges.push({
            id: `e-${dest.id}-${newNode.id}`,
            source: dest.id,
            target: newNode.id,
            animated: true,
            markerEnd: {
              type: 'arrowclosed',
              width: 20,
              height: 20,
              color: '#9c27b6',
            },
            style: { stroke: '#9c27b6', strokeWidth: 2 },
          });
        });

        sourceNodes.forEach((src) => {
          newEdges.push({
            id: `e-${newNode.id}-${src.id}`,
            source: newNode.id,
            target: src.id,
            animated: true,
            markerEnd: {
              type: 'arrowclosed',
              width: 20,
              height: 20,
              color: '#9c27b6',
            },
            style: { stroke: '#9c27b6', strokeWidth: 2 },
          });
        });
      }

      dispatch(setNodes([...nodes, newNode]));
      if (newEdges.length > 0) {
        dispatch(setEdges([...edges, ...newEdges]));
      }
      setClearSourceSelection((p) => p + 1);
    },
    [nodes, edges, dispatch, reactFlowInstance, darkMode],
  );

  const handleNodeDoubleClick = (event, node) => {
    setSelectedNode(node);
    // Reset toast ref when node is selected
    singleSourceMultiDestToastShownRef.current = false;

    if (node.type === 'integrationNode' && /^decision-/i.test(node.id)) {
      setOpenDecisionDrawer(false);
    }
    // Backtrack nodes open configuration modal on double-click
    if (node.type === 'backtrackNode') {
      // Modal will open automatically via selectedNode state
    }
  };

  const DOUBLE_MS = 220;
  const lastClickRef = useRef({ id: null, t: 0 });

  const handleNodeClick = useCallback((e, node) => {
    const now = performance.now();
    const { id, t } = lastClickRef.current;

    if (id === node.id && now - t < DOUBLE_MS) {
      setSelectedNode(node);
      // Reset toast ref when node is selected
      singleSourceMultiDestToastShownRef.current = false;
      lastClickRef.current = { id: null, t: 0 };
      return;
    }
    lastClickRef.current = { id: node.id, t: now };
  }, []);

  const handleSaveConfig = () => {
    setSelectedNode(null);
  };

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (reactFlowInstance.current && nodes.length > 0) {
  //       reactFlowInstance.current.fitView({
  //         padding: 0.3,
  //         includeHiddenNodes: true,
  //       });
  //     }
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [nodes.length, edges.length]);

  // working code of dynamic tabs function

  // const handleLoadWorkflow = (name) => {
  //   dispatch(loadWorkflow({ name }));
  // };

  // const handleDelete = (workflowname) => {
  //   dispatch(deleteWorkflow(workflowname));
  //   // alert(`Deleted workflow: ${workflowName}`);
  //   toast.success(`Deleted workflow: ${workflowname}`);
  // };

  console.log('node format:', {
    nodes: formattedNodes,
    edges: formattedEdges,
    mappings: connectionDetails,
    entitySelections: sanitizedEntitySelections,
  });
  const handleWorkflowSave = async () => {
    if (!connectionDetails || connectionDetails.length === 0) {
      toast.error('No mapping found! Please configure mapping before saving.', {
        autoClose: 3000,
      });
      return;
    }

    // Validate backtrack nodes
    const backtrackNodes = nodes.filter((n) => n.type === 'backtrackNode');
    if (backtrackNodes.length > 1) {
      toast.error('Only 1 backtrack node is allowed per workflow', {
        autoClose: 3000,
      });
      return;
    }

    // Auto-enable feature flag if backtrack nodes exist (should already be enabled by useEffect, but double-check)
    if (backtrackNodes.length > 0 && !enableBacktrack) {
      setEnableBacktrack(true);
    }

    try {
      // dispatch(
      //   saveWorkflow({
      //     name: workflowname,
      //     nodes,
      //     edges,
      //     connectionDetails,
      //   })
      // );

      dispatch(
        setFinalWorkflowJson({
          workflowId,
          // workflowJson: saveJson,
          workflowJson: {
            nodes: formattedNodesWithFilter,
            edges: formattedEdges,
            mappings,
            entitySelections: sanitizedEntitySelections,
            filterExpression: filterExpression || null,
            ...(scheduleDateTime && timeZone
              ? {
                  frequency: workflowData?.frequency
                    ? String(workflowData.frequency).toUpperCase()
                    : null,
                  start_date: scheduleDateTime.split('T')[0],
                  end_date: workflowData?.endDate || null,
                  time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
                  timezone: timeZone,
                  ...(String(workflowData?.frequency).toUpperCase() ===
                    'HOURLY' && workflowData?.hourInterval
                    ? { hour_interval: Number(workflowData.hourInterval) }
                    : {}),
                  ...(['WEEKLY', 'MONTHLY'].includes(
                    String(workflowData?.frequency).toUpperCase(),
                  ) &&
                  Array.isArray(workflowData?.customDays) &&
                  workflowData.customDays.length > 0
                    ? { days_of_week: workflowData.customDays }
                    : {}),
                }
              : {}),
          },
        }),
      );

      console.log('nodes of formating new :', {
        nodes: formattedNodes,
        edges: formattedEdges,
        mappings,
        entitySelections: sanitizedEntitySelections,
        run_at: scheduleDateTime,
        timezone: timeZone,
      });

      // ✅ Get full workflow info from list[] by name
      const allWorkflows = store.getState().workflows.list; // or move to useSelector
      const matched = allWorkflows.find((w) => w.workflow_id === workflowId);

      if (!matched) {
        toast.error('Workflow metadata not found!');
        return;
      }

      // Pull optional recurrence details from store
      const wfState = store.getState().workflows.workflows?.[workflowId] || {};
      const recurrenceFrequency = wfState.frequency || null;
      const recurrenceEndDate = wfState.endDate || null;
      const recurrenceHourInterval = wfState.hourInterval || null;
      const recurrenceDaysOfWeek = wfState.customDays || null;

      const payload = {
        workflow_id: matched.workflow_id ?? null,
        user_id: matched.user_id,
        project_id: matched.project_id,
        name: matched.name,
        description: matched.description || 'auto-saved workflow',
        environment: matched.environment || 'test',
        is_active: matched.is_active ?? true,
        config: {
          nodes: formattedNodesWithFilter,
          edges: formattedEdges,
          mappings,
          entitySelections: sanitizedEntitySelections,
          ...(backtrackNodes.length > 0 || enableBacktrack
            ? {
                featureFlags: {
                  enableBacktrack: enableBacktrack || backtrackNodes.length > 0,
                },
              }
            : {}),
          ...(scheduleDateTime && timeZone
            ? {
                frequency: recurrenceFrequency
                  ? String(recurrenceFrequency).toUpperCase()
                  : null,
                start_date: scheduleDateTime.split('T')[0],
                end_date: recurrenceEndDate || null,
                time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
                timezone: timeZone,
                ...(String(recurrenceFrequency).toUpperCase() === 'HOURLY' &&
                recurrenceHourInterval
                  ? { hour_interval: Number(recurrenceHourInterval) }
                  : {}),
                ...(['WEEKLY', 'MONTHLY'].includes(
                  String(recurrenceFrequency).toUpperCase(),
                ) &&
                Array.isArray(recurrenceDaysOfWeek) &&
                recurrenceDaysOfWeek.length > 0
                  ? { days_of_week: recurrenceDaysOfWeek }
                  : {}),
              }
            : {}),
        },
        // config: saveJson,
      };

      console.log('payload:::', payload);
      console.log('payload.config::', payload.config);
      const response = await postWorkflowToBackend(payload);
      console.log('Workflow saved to backend:', response);

      const savedWorkflowId = response?.workflow_id ?? workflowId;
      const savedWorkflowConfigurationId =
        response?.workflow_configuration_id ?? workflowConfigurationId;

      // setLastWorkflowId(response.workflow_id);

      // dispatch(loadWorkflow({ name: workflowname }));

      dispatch(
        saveWorkflow({
          nodes,
          edges,
          connectionDetails,
          workflowId: savedWorkflowId,
          workflowConfigurationId: savedWorkflowConfigurationId,
          ...(workflowRunId && { workflowRunId }),
        }),
      );

      dispatch(clearWorkflowDirty({ workflowId: savedWorkflowId }));
      dirtySnapshotRef.current = dirtySnapshot;
      dirtyInitializedRef.current = true;

      toast.success('Workflow saved successfully!', {
        autoClose: 2000,
      });

      if (scheduleDateTime && timeZone && response.workflow_id) {
        // await triggerExecuteWorkflow(response.workflow_id);

        const triggerResponse = await triggerExecuteWorkflow(
          response.workflow_id,
          {
            frequency: recurrenceFrequency
              ? String(recurrenceFrequency).toUpperCase()
              : null,
            start_date: scheduleDateTime.split('T')[0],
            end_date: recurrenceEndDate || null,
            time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
            timezone: timeZone,
            ...(String(recurrenceFrequency).toUpperCase() === 'HOURLY' &&
            recurrenceHourInterval
              ? { hour_interval: Number(recurrenceHourInterval) }
              : {}),
            ...(['WEEKLY', 'MONTHLY'].includes(
              String(recurrenceFrequency).toUpperCase(),
            ) &&
            Array.isArray(recurrenceDaysOfWeek) &&
            recurrenceDaysOfWeek.length > 0
              ? { days_of_week: recurrenceDaysOfWeek }
              : {}),
          },
        );

        dispatch(
          setTriggerResponse({
            workflowId,
            triggerResponse,
          }),
        );
        toast.success('Workflow scheduled', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Failed to save workflow:', error);
      toast.error('Error saving workflow to backend.', {
        autoClose: 3000,
      });
    }
  };

  // Expose handleWorkflowSave function via ref
  useImperativeHandle(ref, () => ({
    handleWorkflowSave,
  }));

  console.log(
    'batch delte:',
    nodes.filter((n) => n.selected),
  );

  const selectedNodes = nodes.filter((n) => n.selected);

  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

  useEffect(() => {
    if (reactFlowInstance.current) {
      const { x, y, zoom } = reactFlowInstance.current.getViewport();
      setViewport({ x, y, zoom });
    }
  }, [nodes, edges]);

  // Handle window resize and settings panel changes
  useEffect(() => {
    const handleResize = () => {
      if (reactFlowInstance.current) {
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          reactFlowInstance.current.fitView({
            padding: 0.2,
            includeHiddenNodes: false,
            minZoom: 0.3,
            maxZoom: 1.0,
          });
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle settings panel open/close - refit view when canvas size changes
  useEffect(() => {
    if (reactFlowInstance.current) {
      // Small delay to ensure layout has updated
      const timer = setTimeout(() => {
        reactFlowInstance.current.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.3,
          maxZoom: 1.0,
        });
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [selectedNode]); // Trigger when settings panel opens/closes

  function flowToDom({ x, y }) {
    return {
      x: x * viewport.zoom + viewport.x,
      y: y * viewport.zoom + viewport.y,
    };
  }

  const selectionBox = getSelectionBoundingBox(selectedNodes);

  function getSelectionBoundingBox(nodes) {
    if (nodes.length === 0) return null;
    const minX = Math.min(...nodes.map((n) => n.position.x));
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxX = Math.max(
      ...nodes.map((n) => n.position.x + (n.measured?.width || 56)),
    );
    const maxY = Math.max(
      ...nodes.map((n) => n.position.y + (n.measured?.height || 56)),
    );
    return { minX, minY, maxX, maxY };
  }

  console.log('selectionBox:::', selectionBox);

  // const handleBatchDelete = () => {
  //   const selectedIds = selectedNodes.map((n) => n.id);
  //   const updatedNodes = nodes.filter((n) => !selectedIds.includes(n.id));
  //   dispatch(setNodes(updatedNodes));
  //   const updatedEdges = edges.filter(
  //     (e) => !selectedIds.includes(e.source) && !selectedIds.includes(e.target)
  //   );
  //   dispatch(setEdges(updatedEdges));

  //     selectedNodes.forEach((node) => {
  //   if (
  //     node.type === 'sourceNode' ||
  //     node.type === 'destinationNode' ||
  //     node.type === 'integrationNode'
  //   ) {
  //     const conn_id = node.data?.config?.connection?.conn_id || '';
  //     const module = node.data?.config?.module || '';
  //     const key = `${conn_id}-${module}`;
  //     dispatch(removeEntitiesForKey({ workflowName: workflowname, key }));
  //     dispatch(resetMappingForWorkflow(workflowname));
  //   }
  // });
  // };

  const handleBatchDelete = () => {
    // Get the selected nodes' ids and labels
    const selectedIds = selectedNodes.map((n) => n.id);
    const selectedLabels = selectedNodes.map((n) => n.data.label);

    // Set the nodes to be deleted in the confirmation dialog
    setPendingDeleteNodeIds(selectedIds);
    setPendingDeleteNodeLabel(selectedLabels);

    // Open the confirmation dialog
    setConfirmDialogOpen(true);
  };

  //eslint-disable-next-line
  const deleteNode = (nodeId) => {
    console.log('ACTUAL NODE DELETE FIRED', nodeId);

    const node = nodes.find((n) => n.id === nodeId);

    const updatedNodes = nodes.filter((node) => node.id !== nodeId);
    dispatch(setNodes(updatedNodes));

    const updatedEdges = edges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId,
    );

    dispatch(setEdges(updatedEdges));

    console.log('delete node data::::::', node);

    if (
      node &&
      (node.type === 'sourceNode' ||
        node.type === 'destinationNode' ||
        node.type === 'integrationNode')
    ) {
      console.log('node config:', node.data);
      const conn_id = node.data?.config?.connection?.conn_id || '';
      const module = node.data?.config?.module || '';
      const key = `${node.id}-${conn_id}-${module}`;
      const key1 = `${node.id}`;
      // const workflowName = workflowname;

      console.log('delete node data 1::::::', node);

      dispatch(removeEntitiesForKey({ workflowId, key }));
      dispatch(removeFinalConfigById({ workflowId, id: key1 }));
      dispatch(resetMappingForWorkflow(workflowId));
      dispatch(
        setConfirmedSourceEntities({
          workflowId,
          key: key,
          entities: [],
        }),
      );
      dispatch(
        setConfirmedDestinationEntities({
          workflowId,
          key: key,
          entities: [],
        }),
      );
    }
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteNodeId) {
      // For single node deletion
      deleteNode(pendingDeleteNodeId);
    } else if (pendingDeleteNodeIds.length > 0) {
      // For multiple node deletion
      const updatedNodes = nodes.filter(
        (n) => !pendingDeleteNodeIds.includes(n.id),
      );
      dispatch(setNodes(updatedNodes));

      const updatedEdges = edges.filter(
        (e) =>
          !pendingDeleteNodeIds.includes(e.source) &&
          !pendingDeleteNodeIds.includes(e.target),
      );
      dispatch(setEdges(updatedEdges));

      selectedNodes.forEach((node) => {
        if (
          node.type === 'sourceNode' ||
          node.type === 'destinationNode' ||
          node.type === 'integrationNode'
        ) {
          const conn_id = node.data?.config?.connection?.conn_id || '';
          const module = node.data?.config?.module || '';
          const key = `${node.id}-${conn_id}-${module}`;
          const key1 = `${node.id}`;

          console.log('delete node data 2::::::', node);
          console.log('key1::::::', key1);

          dispatch(removeEntitiesForKey({ workflowId, key }));
          dispatch(removeFinalConfigById({ workflowId, id: key1 }));
          dispatch(
            setConfirmedSourceEntities({
              workflowId,
              key: key,
              entities: [],
            }),
          );
          dispatch(
            setConfirmedDestinationEntities({
              workflowId,
              key: key,
              entities: [],
            }),
          );

          dispatch(resetMappingForWorkflow(workflowId));
        }
      });
    }

    // Close the dialog and reset the pending deletion data
    setConfirmDialogOpen(false);
    setPendingDeleteNodeId(null);
    setPendingDeleteNodeLabel('');
    setPendingDeleteNodeIds([]);
  };
  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setPendingDeleteNodeId(null);
    setPendingDeleteNodeLabel('');
    setPendingDeleteNodeIds([]);
  };

  const updateNodeData = (nodeId, newConfig) => {
    const updatedNodes = nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const existingConfig = node.data?.config || {};

      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...existingConfig,
            ...newConfig,
          },
        },
      };
    });

    dispatch(setNodes(updatedNodes));
  };

  const handleRequestSqlConnectionFix = useCallback(
    (payload = {}) => {
      if (!payload?.nodeId) return;
      const targetNode = nodes.find((node) => node.id === payload.nodeId);
      if (!targetNode) return;

      const fallbackHost =
        targetNode.data?.config?.sqlMetadata?.host ||
        targetNode.data?.config?.connection?.sqlMetadata?.host ||
        '';
      const fallbackPort =
        targetNode.data?.config?.sqlMetadata?.port ||
        targetNode.data?.config?.connection?.sqlMetadata?.port ||
        '';

      setSqlHostPortDialog({
        nodeId: targetNode.id,
        connId:
          payload.connId ||
          targetNode.data?.config?.connection?.conn_id ||
          null,
        connectionName:
          payload.connectionName ||
          targetNode.data?.config?.connection?.conn_name ||
          targetNode.data?.label ||
          targetNode.data?.config?.label ||
          'SQL Connection',
        host: payload.host || fallbackHost,
        port: payload.port || fallbackPort,
      });
    },
    [nodes],
  );

  // Create a merged theme that includes both the base theme and dark mode settings
  const mergedTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      mode: darkMode ? 'dark' : 'light',
      background: {
        ...theme.palette.background,
        default: darkMode ? '#121212' : '#ffffff',
        paper: darkMode ? '#282828' : '#ffffff',
      },
      text: {
        ...theme.palette.text,
        primary: darkMode ? '#ffffff' : '#000000',
      },
    },
  };

  console.log('nodes in Redux:', nodes);
  console.log('edges in Redux:', edges);

  const destinationOptions = useMemo(() => {
    const nodesArr = Array.isArray(nodes) ? nodes : [];
    const opts = nodesArr
      .filter((n) => n?.type === 'destinationNode')
      .map((n) => {
        const raw =
          n?.data?.label ?? n?.label ?? n?.data?.config?.label ?? n?.id;

        const label = String(raw).replace(/\s*\(.*?\)\s*$/, '') || String(n.id);
        return { value: n.id, label };
      })
      .filter((o) => o.label);

    // de-dupe by value
    const seen = new Set();
    return opts.filter((o) =>
      seen.has(o.value) ? false : (seen.add(o.value), true),
    );
  }, [nodes]);

  return (
    <ReactFlowProvider>
      <ThemeProvider theme={mergedTheme}>
        <Box
          sx={{
            bgcolor: darkMode ? 'background.default' : '#f8f6ffff',
            color: darkMode ? 'text.primary' : 'text.primary',
            minHeight: '100vh',
            width: '100%',
          }}
        >
          <WorkflowActionBar
            onRunNow={() => actionRef.current?.runNow()}
            onScheduleOpen={() => actionRef.current?.openSchedule()}
            workflowId={workflowId}
            setWorkflowName={setWorkflowName}
            onSave={handleWorkflowSave}
            enableBacktrack={enableBacktrack}
            setEnableBacktrack={setEnableBacktrack}
          />

          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              bgcolor: darkMode ? '#121212' : undefined,
            }}
          >
            {/* Sidebar */}
            <Sidebar
              ref={sidebarRef}
              workflowId={workflowId}
              setWorkflowName={setWorkflowName}
              onSave={handleWorkflowSave}
              clearSourceSelection={clearSourceSelection}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={setCollapsedBoth}
            />

            <Box
              sx={{
                display: 'flex',
                flexGrow: 1,
                // height: 'calc(100vh - 48px)',
                flexWrap: 'wrap',
                height: 'auto',
                marginTop: '40px',
                minHeight: 'calc(100vh - 60px)',
                overflow: 'hidden',
                bgcolor: darkMode ? '#121212' : undefined,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  // flexGrow:
                  //   selectedNode &&
                  //   (selectedNode.type === 'sourceNode' ||
                  //     selectedNode.type === 'destinationNode')
                  //     ? 0.7
                  //     : 1,
                  flexGrow: 1,
                  // transition: 'flex-grow 0.3s ease-in-out',
                  transition: 'none',
                  width: '100%',
                  // maxWidth:
                  //   selectedNode &&
                  //   (selectedNode.type === 'sourceNode' ||
                  //     selectedNode.type === 'destinationNode')
                  //     ? 'calc(100% - 160px)'
                  //     : '100%',
                  maxWidth: '100%',
                  bgcolor: darkMode ? '#121212' : undefined,
                  pt: 2,
                }}
              >
                {/* {Object.keys(workflows).length > 1 && (
                  <Box
                    sx={{
                      backgroundColor: darkMode ? '#282828' : '#f5f5f5',
                      pl: 2,
                      py: 0.2,
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    <Tabs
                      value={selectedFile}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTabs-indicator': {
                          backgroundColor: 'primary.main',
                        },
                      }}
                    >
                      {Object.keys(workflows).map((name) => (
                        <Tab
                          key={name}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {name}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(name);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                          value={name}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            minHeight: '30px',
                            minWidth: '100px',
                            px: 2,
                            '&.Mui-selected': {
                              backgroundColor: darkMode ? '#1e2738' : 'secondary.default',
                              borderRadius: '4px 4px 0 0',
                            },
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                )} */}

                {/* <IntegrationPanelPill /> */}

                <Box
                  ref={reactFlowWrapper}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setCollapsedBoth((v) => !v);
                  }}
                  onClick={() => setClearSourceSelection((p) => p + 1)}
                  sx={{
                    flexGrow: 1,
                    width:
                      selectedNode && selectedNode.type !== 'integrationNode'
                        ? '98%'
                        : '98%',
                    background: darkMode ? '#1e1e1e' : '#fff',
                    position: 'relative',
                    border: `2px dashed ${darkMode ? '#555' : '#555'}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    m: 1,
                    transition: 'width 0.3s ease-in-out',
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                >
                  <ReactFlow
                    onMove={(_, viewport) => setViewport(viewport)}
                    edgeTypes={edgeTypes}
                    nodes={nodes}
                    onNodesChange={onNodesChange}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onEdgeClick={onEdgeClick}
                    nodeTypes={nodeTypes}
                    onInit={(instance) => {
                      reactFlowInstance.current = instance;
                      // instance.setViewport({ x: 0, y: 0, zoom: 0.6 });
                    }}
                    onNodeDoubleClick={handleNodeDoubleClick}
                    onNodeClick={handleNodeClick}
                    fitView
                    edgesOptions={{
                      animated: true,
                      style: { stroke: darkMode ? '#3a8eff' : '#1976d2' },
                    }}
                    maxZoom={0.8}
                    minZoom={0.6}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
                  >
                    <Background
                      color={darkMode ? '#555' : '#aaa'}
                      gap={54}
                      size={3}
                      variant={darkMode ? 'dots' : 'dots'}
                    />
                    <Controls
                      style={{
                        background: darkMode ? '#282828' : '#fff',
                        borderColor: darkMode ? '#555' : '#ccc',
                        color: darkMode ? '#fff' : '#000',
                      }}
                    />
                  </ReactFlow>
                  {/* <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 5,
                      pointerEvents: 'auto',
                    }}
                  >
                    <IntegrationPanelPill />
                  </Box> */}

                  {selectionBox &&
                    selectedNodes.length > 1 &&
                    (() => {
                      const center = {
                        x: (selectionBox.minX + selectionBox.maxX) / 2,
                        y: selectionBox.minY + 80,
                      };

                      const domPos = flowToDom(center);

                      return (
                        <IconButton
                          onClick={handleBatchDelete}
                          sx={{
                            position: 'absolute',
                            left: domPos.x - 18, // adjust for button width
                            top: domPos.y - 38, // above the selection box
                            zIndex: 1000,
                            bgcolor: 'white',
                            border: '1.5px solid #d32f2f',
                            color: '#d32f2f',
                            boxShadow: 2,
                            '&:hover': { bgcolor: '#d32f2f', color: 'white' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      );
                    })()}
                </Box>
              </Box>

              {selectedNode &&
              (selectedNode.type === 'sourceNode' ||
                selectedNode.type === 'destinationNode') ? (
                <Dialog
                  open={!!selectedNode}
                  onClose={() => setSelectedNode(null)}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                    },
                  }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={() => setSelectedNode(null)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: '#666',
                      '&:hover': { color: '#000' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  <DialogContent sx={{ p: 0 }}>
                    <NodeConfigurationPanel
                      selectedNode={selectedNode}
                      setSelectedNode={setSelectedNode}
                      handleSaveConfig={handleSaveConfig}
                      updateNodeData={updateNodeData}
                      onConfigUpdate={setNodeConfigForEntitySelection}
                    />
                  </DialogContent>
                </Dialog>
              ) : null}

              <SqlHostPortDialog
                open={Boolean(sqlHostPortDialog)}
                connectionName={sqlHostPortDialog?.connectionName}
                initialHost={sqlHostPortDialog?.host}
                initialPort={sqlHostPortDialog?.port}
                connId={sqlHostPortDialog?.connId}
                onClose={() => setSqlHostPortDialog(null)}
                onUpdated={({ host, port }) => {
                  if (!sqlHostPortDialog?.nodeId) {
                    setSqlHostPortDialog(null);
                    return;
                  }
                  const targetNode = nodes.find(
                    (node) => node.id === sqlHostPortDialog.nodeId,
                  );
                  updateNodeData(sqlHostPortDialog.nodeId, {
                    sqlMetadata: {
                      ...(targetNode?.data?.config?.sqlMetadata || {}),
                      host,
                      port,
                    },
                  });
                  setSqlHostPortDialog(null);
                }}
              />

              {selectedNode && selectedNode.type === 'backtrackNode' ? (
                <Dialog
                  open={!!selectedNode}
                  onClose={() => setSelectedNode(null)}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                    },
                  }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={() => setSelectedNode(null)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: '#666',
                      '&:hover': { color: '#000' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  <DialogContent sx={{ p: 0 }}>
                    <BacktrackConfigurationModal
                      selectedNode={selectedNode}
                      setSelectedNode={setSelectedNode}
                      handleSaveConfig={handleSaveConfig}
                      updateNodeData={updateNodeData}
                      updatedConfig={saveJson}
                      onConfigUpdate={setNodeConfigForEntitySelection}
                      workflowId={workflowId}
                      nodes={nodes}
                      edges={edges}
                    />
                  </DialogContent>
                </Dialog>
              ) : null}

              <InfoDialog
                open={precheckOpen}
                title="Before you continue"
                content={`Define at least ${requiredEntityCount} ${
                  requiredEntityCount === 1 ? 'condition' : 'conditions'
                }, ensuring one for each selected source entity.`}
                onOk={() => {
                  setPrecheckOpen(false);
                  setOpenDecisionDrawer(true);
                }}
                sx={{ zIndex: (t) => t.zIndex.modal + 30 }}
              />

              {selectedNode &&
              selectedNode?.type === 'integrationNode' &&
              selectedNode?.id?.startsWith('decision-') &&
              openDecisionDrawer ? (
                <Box
                  sx={{
                    minWidth: '50px',
                    background: darkMode ? '#282828' : 'white',
                    overflowY: 'auto',
                    height: '100%',
                    transition: 'width 0.3s ease-in-out',
                    color: darkMode ? 'white' : 'inherit',
                  }}
                >
                  <DecisionConfigurationModal
                    selectedNode={selectedNode}
                    setSelectedNode={setSelectedNode}
                    handleSaveConfig={handleSaveConfig}
                    updateNodeData={updateNodeData}
                    onConfigUpdate={setNodeConfigForEntitySelection}
                    sourceResolved={decisionFields}
                    destinationOptions={destinationOptions}
                    onSaveAndNext={() => {
                      const info = getMappingEndpoints(
                        selectedNode,
                        nodes,
                        edges,
                      );
                      setMappingDialogData(info); // {source, dest1, dest2, sourceId, dest1Id, dest2Id}
                      setMappingDialogOpen(true);
                    }}
                    connectedDestinations={getMappingEndpoints(
                      selectedNode,
                      nodes,
                      edges,
                    )}
                    onClose={() => setOpenDecisionDrawer(false)}
                  />
                </Box>
              ) : null}

              {selectedNode?.type === 'integrationNode' &&
                // /^decision-/i.test(selectedNode?.id) &&
                !openDecisionDrawer &&
                (() => {
                  const integrationNodeId = selectedNode.id;

                  // const connectedEdges = edges.filter(
                  //   (edge) =>
                  //     edge.source === integrationNodeId ||
                  //     edge.target === integrationNodeId
                  // );

                  // const connectedNodeIds = connectedEdges.map((edge) =>
                  //   edge.source === integrationNodeId
                  //     ? edge.target
                  //     : edge.source
                  // );

                  // const connectedSource = nodes.find(
                  //   (node) =>
                  //     connectedNodeIds.includes(node.id) &&
                  //     node.type === 'sourceNode'
                  // );
                  // const connectedDestination = nodes.find(
                  //   (node) =>
                  //     connectedNodeIds.includes(node.id) &&
                  //     node.type === 'destinationNode'
                  // );

                  // const isSourceConfigured =
                  //   connectedSource?.data?.config?.connection &&
                  //   connectedSource?.data?.config?.module;

                  // const isDestinationConfigured =
                  //   connectedDestination?.data?.config?.connection &&
                  //   connectedDestination?.data?.config?.module;

                  // const canOpenDialog =
                  //   connectedSource &&
                  //   connectedDestination &&
                  //   isSourceConfigured &&
                  //   isDestinationConfigured;

                  const sources = nodes.filter((n) => n.type === 'sourceNode');
                  const dests = nodes.filter(
                    (n) => n.type === 'destinationNode',
                  );

                  console.log('sql: sources', sources);
                  console.log('sql: dests', dests);

                  const hasAny = sources.length > 0 && dests.length > 0;

                  // const allSrcConfigured = sources.every(
                  //   (sn) =>
                  //     sn.data?.config?.connection && sn.data?.config?.module,
                  // );
                  // const allDestConfigured = dests.every(
                  //   (dn) =>
                  //     dn.data?.config?.connection && dn.data?.config?.module,
                  // );

                  const isSqlLike = (n) => {
                    const lbl = (n?.data?.label || '').toLowerCase();
                    return lbl.includes('sql') || lbl.includes('postgres');
                  };

                  const allSrcConfigured = sources.every((sn) => {
                    const cfg = sn.data?.config || {};

                    if (!cfg.connection) return false;

                    if (isSqlLike(sn)) {
                      return true;
                    }

                    return !!cfg.module;
                  });

                  const allDestConfigured = dests.every((dn) => {
                    const cfg = dn.data?.config || {};

                    if (!cfg.connection) return false;

                    if (isSqlLike(dn)) {
                      return true;
                    }

                    return !!cfg.module;
                  });

                  const allSrcEdges = sources.every((sn) =>
                    edges.some(
                      (e) =>
                        e.source === sn.id && e.target === integrationNodeId,
                    ),
                  );
                  const allDestEdges = dests.every((dn) =>
                    edges.some(
                      (e) =>
                        e.source === integrationNodeId && e.target === dn.id,
                    ),
                  );

                  // Check connected sources and destinations for this integration node
                  const connectedSources = sources.filter((sn) =>
                    edges.some(
                      (e) =>
                        e.source === sn.id && e.target === integrationNodeId,
                    ),
                  );
                  const connectedDests = dests.filter((dn) =>
                    edges.some(
                      (e) =>
                        e.source === integrationNodeId && e.target === dn.id,
                    ),
                  );

                  // Check if it's a decision node
                  const isDecisionNode =
                    /^decision-/i.test(selectedNode?.id) ||
                    /^decision/i.test(selectedNode?.data?.label || '');

                  // Validation: Single source + multiple destinations + all configured + not a decision node
                  const isSingleSourceMultipleDest =
                    !isDecisionNode &&
                    connectedSources.length === 1 &&
                    connectedDests.length > 1 &&
                    allSrcConfigured &&
                    allDestConfigured &&
                    allSrcEdges &&
                    allDestEdges;

                  // Prevent dialog opening for single source + multiple destinations scenario
                  // Toast is handled by useEffect above
                  if (isSingleSourceMultipleDest) {
                    return null;
                  }

                  const canOpenDialog =
                    hasAny &&
                    allSrcConfigured &&
                    allDestConfigured &&
                    allSrcEdges &&
                    allDestEdges;

                  return canOpenDialog ? (
                    <EntitySelectionDialog
                      requireEntityChangeForNext={false}
                      // open={true}
                      open={!precheckOpen && !openDecisionDrawer}
                      selectedNode={selectedNode}
                      onClose={() => setSelectedNode(null)}
                      onNext={(fields) => {
                        setDecisionFields(Array.isArray(fields) ? fields : []);
                        // setOpenDecisionDrawer(true);
                        console.log('click');
                        const uniq = new Set(
                          (fields || []).map((f) => String(f.entity)),
                        ).size;
                        setRequiredEntityCount(uniq || 1);
                        setPrecheckOpen(true);
                        console.log('click 2');
                      }}
                      updatedConfig={processWorkflowDatas}
                      hideAutoFuzzy={
                        (selectedNode?.type === 'integrationNode' ||
                          selectedNode?.type === 'integration') &&
                        /^decision-/i.test(selectedNode?.id)
                      }
                      onRequestSqlConnectionFix={handleRequestSqlConnectionFix}
                    />
                  ) : null;
                })()}
            </Box>

            <RightSideDrawer
              ref={rightSidebarRef}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={setCollapsedBoth}
            />
          </Box>

          {/* <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'center',
              px: 2, // padding on left/right
              pb: 1,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 'calc(100% - 300px)',
                ml: 'auto',
              }}
            >
              {connectionDetails?.length > 0 && (
                <Accordion
                  sx={{
                    mb: 2,
                    bgcolor: darkMode ? '#1e1e1e' : '#f9f9f9',
                    color: darkMode ? '#fff' : '#000',
                    borderRadius: 2,
                    // boxShadow: darkMode ? '0 0 10px #000' : '0px 2px 8px rgba(0,0,0,0.1)',
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                  }}
                  defaultExpanded
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">
                      🧾 Mapping Preview (JSON)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 2,
                      backgroundColor: darkMode ? '#1e1e1e' : '#fff',
                      borderRadius: 1,
                      overflowX: 'auto',
                    }}
                  >
                    <Box
                      component="pre"
                      sx={{
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        m: 0,
                       
                      }}
                    >
                      {JSON.stringify(connectionDetails, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Box> */}

          <ActionButtons
            ref={actionRef}
            selectedNode={selectedNode}
            workflowId={workflowId}
            workflowConfigurationId={workflowConfigurationId}
            // workflowName={workflowId}
            onWorkflowExecutionComplete={handleWorkflowSave}
            onSave={handleWorkflowSave}
            collapsedBoth={collapsedBoth}
            onCollapsedChange={setCollapsedBoth}
            leftSidebarRef={sidebarRef.current?.isLeftCollapsed}
            rightSidebarRef={rightSidebarRef.current?.isRightCollapsed}
          />
        </Box>
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Confirm Node Deletion"
          content={`Are you sure you want to remove the node "${pendingDeleteNodeLabel}"?`}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
        {/* <ProceedMappingDialog
          open={mappingDialogOpen}
          onClose={handleCloseMapping}
          onProceed={handleProceedAndNavigate}
          title="Proceeding to Mapping 1"
          source={mappingDialogData.source}
          destination1={mappingDialogData.dest1}
          destination2={mappingDialogData.dest2}
        /> */}

        <ProceedMappingDialog
          open={mappingDialogOpen}
          onClose={handleCloseMapping}
          onProceed={(idx) => handleProceedAndNavigate(idx)}
          mappings={(mappingDialogData?.destIds || []).map((_, i) => ({
            sourceLabel: mappingDialogData?.source || 'Source',
            destinationLabel:
              mappingDialogData?.destNames?.[i] || `Mapping ${i + 1}`,
          }))}
          activeIndex={Math.max(
            0,
            (mappingDialogData?.destIds || []).indexOf(
              mappingDialogData?.dest1Id,
            ),
          )}
        />
      </ThemeProvider>
    </ReactFlowProvider>
  );
});

ETLWorkflow.displayName = 'ETLWorkflow';
export default ETLWorkflow;
